<template>
  <Teleport to="body">
    <div class="modal-backdrop" @click.self="$emit('close')">
      <div class="modal-container">
        <div class="modal-content export-modal">
          <div class="modal-header">
            <div class="modal-title-section">
              <h2 class="modal-title">{{ currentStep === 'review' ? 'Review Selected Invoices' : $t('export.title') }}</h2>
              <p v-if="companyName" class="modal-company-name">{{ companyName }}</p>
            </div>
            <button @click="$emit('close')" class="btn btn-icon">
              <LineIcon name="x" :size="15" :stroke-width="1.8" />
            </button>
          </div>

      <div class="modal-body">
        <!-- Step 1: Review Invoices -->
        <div v-if="currentStep === 'review'" class="review-step">
          <p class="export-info">
            Review the {{ invoiceCount }} invoice{{ invoiceCount > 1 ? 's' : '' }} you are about to export:
          </p>

          <!-- Invalid VAT Warning -->
          <div v-if="!loadingInvoices && invalidVatCount > 0" class="invalid-vat-warning">
            <LineIcon name="alert-triangle" :size="15" :stroke-width="1.8" />
            <strong>Invalid VAT Numbers:</strong>
            <span>{{ invalidVatCount }} invoice{{ invalidVatCount > 1 ? 's have' : ' has' }} invalid VAT numbers. Please review and correct before exporting.</span>
          </div>

          <!-- Missing Information Warning -->
          <div v-if="!loadingInvoices && missingInfoInvoices.length > 0" class="missing-info-warning">
            <LineIcon name="alert-triangle" :size="15" :stroke-width="1.8" />
            <strong>Missing Information:</strong>
            <span>{{ missingInfoInvoices.length }} invoice{{ missingInfoInvoices.length > 1 ? 's have' : ' has' }} missing required information. Please review before exporting.</span>
          </div>

          <!-- VAT Conflict Warning -->
          <div v-if="!loadingInvoices && vatConflictCount > 0" class="vat-conflict-warning">
            <LineIcon name="alert-triangle" :size="15" :stroke-width="1.8" />
            <strong>VAT Conflicts:</strong>
            <span>{{ vatConflictCount }} invoice{{ vatConflictCount > 1 ? 's have' : ' has' }} duplicate VAT numbers with different names. Please review before exporting.</span>
          </div>

          <div v-if="loadingInvoices" class="loading-container">
            <div class="spinner-small"></div>
            <span>Loading invoice details...</span>
          </div>

          <div v-else-if="invoicesData.length > 0" class="invoices-table-container">
            <table class="invoices-review-table">
              <thead>
                <tr>
                  <th>Invoice Number</th>
                  <th>Date</th>
                  <th>Company VAT</th>
                  <th>Partner VAT</th>
                  <th>Partner Name</th>
                  <th>Currency</th>
                  <th>VAT Amount</th>
                  <th>Total Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="invoice in sortedInvoicesData" :key="invoice.id" :class="{ 'conflict-vat-row': hasVatConflict(invoice.id) }">
                  <td :class="{ 'missing-data': !invoice.invoice_number || invoice.invoice_number === '' }">
                    <input
                      v-model="invoice.invoice_number"
                      type="text"
                      class="editable-field"
                      placeholder="Invoice Number"
                    />
                  </td>
                  <td>
<DateField v-model="invoice.invoice_date" class="editable-field" />
                  </td>
                  <td :class="{ 'missing-data': !invoice.company_vat || invoice.company_vat === '', 'vat-invalid-cell': getBuyerVatValidation(invoice) === false }">
                    <input
                      v-model="invoice.company_vat"
                      type="text"
                      class="editable-field"
                      placeholder="Company VAT"
                    />
                  </td>
                  <td :class="{ 'missing-data': !invoice.vendor_vat || invoice.vendor_vat === '', 'conflict-vat': hasVatConflict(invoice.id), 'vat-invalid-cell': getSellerVatValidation(invoice) === false }">
                    <input
                      v-model="invoice.vendor_vat"
                      type="text"
                      class="editable-field"
                      placeholder="Partner VAT"
                    />
                  </td>
                  <td :class="{ 'missing-data': !invoice.vendor_name || invoice.vendor_name === '', 'conflict-vat': hasVatConflict(invoice.id), 'unique-name': hasUniqueName(invoice.id) }">
                    <div class="flex items-center gap-1">
                      <input
                        v-model="invoice.vendor_name"
                        type="text"
                        class="editable-field flex-1"
                        placeholder="Partner Name"
                      />
                      <button
                        type="button"
                        @click="openSellerModal(invoice.id)"
                        class="select-seller-btn ml-1"
                        title="Select Seller"
                      >
                        <LineIcon name="users" :size="15" :stroke-width="1.8" class="text-sm" />
                      </button>
                    </div>
                  </td>
                  <td :class="{ 'missing-data': !invoice.currency || invoice.currency === '' }">
                    <input
                      v-model="invoice.currency"
                      type="text"
                      class="editable-field"
                      placeholder="Currency"
                      maxlength="3"
                      style="text-transform: uppercase;"
                    />
                  </td>
                  <td :class="{ 'missing-data': !invoice.vat_amount || invoice.vat_amount === '' }">
                    <input
                      v-model="invoice.vat_amount"
                      type="number"
                      step="0.01"
                      class="editable-field"
                      placeholder="VAT Amount"
                    />
                  </td>
                  <td :class="{ 'missing-data': !invoice.total_amount || invoice.total_amount === '' }">
                    <input
                      v-model="invoice.total_amount"
                      type="number"
                      step="0.01"
                      class="editable-field"
                      placeholder="Total Amount"
                    />
                  </td>
                  <td style="text-align: center;">
                    <button
                      type="button"
                      @click="viewInvoice(invoice.id)"
                      class="btn-view"
                      title="View Invoice"
                    >
                      <LineIcon name="eye" :size="15" :stroke-width="1.8" />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-else class="no-data">
            No invoice data available
          </div>
        </div>

        <!-- Step 2: Select Format -->
        <div v-if="currentStep === 'format'">
          <p class="export-info">
            {{ exportInfo }}
          </p>

        <div class="format-options">
          <div
            v-for="format in formats"
            :key="format.value"
            class="format-option"
            :class="{ 'selected': selectedFormat === format.value, 'loading': loading, 'has-submenu': format.submenu, 'submenu-open': (format.value === 'ajur' && showAjurSubmenu) || (format.value === 'nap' && showNapSubmenu) || (format.value === 'excel_export' && showExcelSubmenu) || (format.value === 'xero' && showXeroSubmenu) || (format.value === 'quickbooks' && showQuickBooksSubmenu) || (format.value === 'tallyprime' && showTallySubmenu) || (format.value === 'original_files' && showOriginalFilesSubmenu) || (format.value === 'business_navigator' && showBusinessNavigatorSubmenu) || (format.value === 'santa' && showSantaSubmenu) }"
            @click="handleFormatClick(format)"
          >
            <div class="format-content">
              <div class="format-icon">
                <LineIcon :name="format.icon" :size="20" :stroke-width="1.8" />
              </div>
              <div class="format-details">
                <div class="format-name">
                  {{ format.name }}
                  <span v-if="format.beta" class="beta-badge">BETA</span>
                </div>
                <div class="format-description">{{ format.description }}</div>
              </div>
              <div v-if="format.submenu" class="format-arrow">
                <LineIcon :name="isSubmenuOpen(format.value) ? 'chevron-up' : 'chevron-down'" :size="13" :stroke-width="1.8" />
              </div>

              <div v-if="loading && selectedFormat === format.value" class="format-loading">
                <div class="spinner-small"></div>
              </div>
            </div>
            
            <!-- Submenu for Ajur options -->
            <div v-if="format.value === 'ajur' && showAjurSubmenu" class="format-submenu" @click.stop>
              <div
                v-for="subformat in format.submenu"
                :key="subformat.value"
                class="submenu-item"
                :class="{ 'selected': selectedFormat === subformat.value }"
                @click="selectFormatAndExport(subformat.value)"
              >
                <div class="submenu-icon">
                  <LineIcon :name="subformat.icon" :size="15" :stroke-width="1.8" />
                </div>
                <div class="submenu-details">
                  <div class="submenu-name">{{ subformat.name }}</div>
                  <div class="submenu-description">{{ subformat.description }}</div>
                </div>
              </div>
            </div>
            
            <!-- Submenu for Original Files options -->
            <div v-if="format.value === 'original_files' && showOriginalFilesSubmenu" class="format-submenu" @click.stop>
              <div
                v-for="subformat in format.submenu"
                :key="subformat.value"
                class="submenu-item"
                :class="{ 'selected': selectedFormat === subformat.value }"
                @click="selectFormatAndExport(subformat.value)"
              >
                <div class="submenu-icon">
                  <LineIcon :name="subformat.icon" :size="15" :stroke-width="1.8" />
                </div>
                <div class="submenu-details">
                  <div class="submenu-name">{{ subformat.name }}</div>
                  <div class="submenu-description">{{ subformat.description }}</div>
                </div>
              </div>
            </div>
            
            <!-- Submenu for NAP options -->
            <div v-if="format.value === 'nap' && showNapSubmenu" class="format-submenu" @click.stop>
              <div
                v-for="subformat in format.submenu"
                :key="subformat.value"
                class="submenu-item"
                :class="{ 'selected': selectedFormat === subformat.value }"
                @click="selectFormatAndExport(subformat.value)"
              >
                <div class="submenu-icon">
                  <LineIcon :name="subformat.icon" :size="15" :stroke-width="1.8" />
                </div>
                <div class="submenu-details">
                  <div class="submenu-name">{{ subformat.name }}</div>
                  <div class="submenu-description">{{ subformat.description }}</div>
                </div>
              </div>
            </div>
            
            <!-- Submenu for Excel options -->
            <div v-if="format.value === 'excel_export' && showExcelSubmenu" class="format-submenu" @click.stop>
              <!-- Excel item -->
              <div
                class="submenu-item"
                :class="{ 'selected': selectedFormat === 'excel', 'type-open': showExcelTypeOptions }"
              >
                <div class="submenu-item-main" @click.stop="toggleExcelTypeOptions('excel')">
                  <div class="submenu-icon">
                    <LineIcon name="table" :size="15" :stroke-width="1.8" />
                  </div>
                  <div class="submenu-details">
                    <div class="submenu-name">{{ format.submenu[0].name }}</div>
                    <div class="submenu-description">{{ format.submenu[0].description }}</div>
                  </div>
                  <div class="submenu-expand-arrow">
                    <LineIcon :name="showExcelTypeOptions ? 'chevron-up' : 'chevron-down'" :size="13" :stroke-width="1.8" />
                  </div>
                </div>
                <button 
                  v-if="!showExcelTypeOptions"
                  type="button"
                  class="submenu-settings-btn"
                  @click.stop.prevent="openColumnsModal"
                  title="Configure columns"
                >
                  <LineIcon name="settings" :size="15" :stroke-width="1.8" />
                </button>
                <div v-if="showExcelTypeOptions" class="type-options" @click.stop>
                  <div class="type-option" @click.stop="exportWithType('excel', 'purchase')">
                    <LineIcon name="shopping-cart" :size="15" :stroke-width="1.8" class="type-option-icon" />
                    <span>{{ t('invoices.purchase') || 'Purchases' }}</span>
                  </div>
                  <div class="type-option" @click.stop="exportWithType('excel', 'sale')">
                    <LineIcon name="banknote" :size="15" :stroke-width="1.8" class="type-option-icon" />
                    <span>{{ t('invoices.sale') || 'Sales' }}</span>
                  </div>
                </div>
              </div>
              <!-- Excel Detailed item -->
              <div
                class="submenu-item"
                :class="{ 'selected': selectedFormat === 'excel_detailed', 'type-open': showExcelDetailedTypeOptions }"
              >
                <div class="submenu-item-main" @click.stop="toggleExcelTypeOptions('excel_detailed')">
                  <div class="submenu-icon">
                    <LineIcon name="table" :size="15" :stroke-width="1.8" />
                  </div>
                  <div class="submenu-details">
                    <div class="submenu-name">{{ format.submenu[1].name }}</div>
                    <div class="submenu-description">{{ format.submenu[1].description }}</div>
                  </div>
                  <div class="submenu-expand-arrow">
                    <LineIcon :name="showExcelDetailedTypeOptions ? 'chevron-up' : 'chevron-down'" :size="13" :stroke-width="1.8" />
                  </div>
                </div>
                <button 
                  v-if="!showExcelDetailedTypeOptions"
                  type="button"
                  class="submenu-settings-btn"
                  @click.stop.prevent="openColumnsModal"
                  title="Configure columns"
                >
                  <LineIcon name="settings" :size="15" :stroke-width="1.8" />
                </button>
                <div v-if="showExcelDetailedTypeOptions" class="type-options" @click.stop>
                  <div class="type-option" @click.stop="exportWithType('excel_detailed', 'purchase')">
                    <LineIcon name="shopping-cart" :size="15" :stroke-width="1.8" class="type-option-icon" />
                    <span>{{ t('invoices.purchase') || 'Purchases' }}</span>
                  </div>
                  <div class="type-option" @click.stop="exportWithType('excel_detailed', 'sale')">
                    <LineIcon name="banknote" :size="15" :stroke-width="1.8" class="type-option-icon" />
                    <span>{{ t('invoices.sale') || 'Sales' }}</span>
                  </div>
                </div>
              </div>
              <!-- DocDB item -->
              <div
                class="submenu-item"
                :class="{ 'selected': selectedFormat === 'docdb' }"
                @click="selectFormatAndExport('docdb')"
              >
                <div class="submenu-item-main">
                  <div class="submenu-icon">
                    <LineIcon name="database" :size="15" :stroke-width="1.8" />
                  </div>
                  <div class="submenu-details">
                    <div class="submenu-name">{{ format.submenu[2].name }}</div>
                    <div class="submenu-description">{{ format.submenu[2].description }}</div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Submenu for Xero options -->
            <div v-if="format.value === 'xero' && showXeroSubmenu" class="format-submenu" @click.stop>
              <div
                v-for="subformat in format.submenu"
                :key="subformat.value"
                class="submenu-item"
                :class="{ 'selected': selectedFormat === subformat.value }"
                @click="selectFormatAndExport(subformat.value)"
              >
                <div class="submenu-icon">
                  <LineIcon :name="subformat.icon" :size="15" :stroke-width="1.8" />
                </div>
                <div class="submenu-details">
                  <div class="submenu-name">{{ subformat.name }}</div>
                  <div class="submenu-description">{{ subformat.description }}</div>
                </div>
              </div>
            </div>
            
            <!-- Submenu for QuickBooks options -->
            <div v-if="format.value === 'quickbooks' && showQuickBooksSubmenu" class="format-submenu" @click.stop>
              <div
                v-for="subformat in format.submenu"
                :key="subformat.value"
                class="submenu-item"
                :class="{ 'selected': selectedFormat === subformat.value }"
                @click="selectFormatAndExport(subformat.value)"
              >
                <div class="submenu-icon">
                  <LineIcon :name="subformat.icon" :size="15" :stroke-width="1.8" />
                </div>
                <div class="submenu-details">
                  <div class="submenu-name">{{ subformat.name }}</div>
                  <div class="submenu-description">{{ subformat.description }}</div>
                </div>
              </div>
            </div>
            
            <!-- Submenu for TallyPrime options -->
            <div v-if="format.value === 'tallyprime' && showTallySubmenu" class="format-submenu" @click.stop>
              <div
                v-for="subformat in format.submenu"
                :key="subformat.value"
                class="submenu-item"
                :class="{ 'selected': selectedFormat === subformat.value }"
                @click="selectFormatAndExport(subformat.value)"
              >
                <div class="submenu-icon">
                  <LineIcon :name="subformat.icon" :size="15" :stroke-width="1.8" />
                </div>
                <div class="submenu-details">
                  <div class="submenu-name">{{ subformat.name }}</div>
                  <div class="submenu-description">{{ subformat.description }}</div>
                </div>
              </div>
            </div>
            
            <!-- Submenu for Business Navigator options -->
            <div v-if="format.value === 'business_navigator' && showBusinessNavigatorSubmenu" class="format-submenu" @click.stop>
              <div
                v-for="subformat in format.submenu"
                :key="subformat.value"
                class="submenu-item"
                :class="{ 'selected': selectedFormat === subformat.value }"
                @click="selectFormatAndExport(subformat.value)"
              >
                <div class="submenu-icon">
                  <LineIcon :name="subformat.icon" :size="15" :stroke-width="1.8" />
                </div>
                <div class="submenu-details">
                  <div class="submenu-name">{{ subformat.name }}</div>
                  <div class="submenu-description">{{ subformat.description }}</div>
                </div>
              </div>
            </div>

            <!-- Submenu for Santa options -->
            <div v-if="format.value === 'santa' && showSantaSubmenu" class="format-submenu" @click.stop>
              <div
                v-for="subformat in format.submenu"
                :key="subformat.value"
                class="submenu-item"
                :class="{ 'selected': selectedFormat === subformat.value }"
                @click="selectFormatAndExport(subformat.value)"
              >
                <div class="submenu-icon">
                  <LineIcon :name="subformat.icon" :size="15" :stroke-width="1.8" />
                </div>
                <div class="submenu-details">
                  <div class="submenu-name">{{ subformat.name }}</div>
                  <div class="submenu-description">{{ subformat.description }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="error" class="error-message">
          {{ error }}
        </div>

        <div v-if="loading" class="loading-message">
          <div class="spinner-small"></div>
          <span>{{ $t('export.exporting') }}</span>
        </div>
        </div>
      </div>

      <div v-if="!loading" class="modal-footer">
        <button v-if="currentStep === 'review' && hasUnsavedChanges" @click="saveChanges" class="btn btn-success" :disabled="saving">
          <span v-if="!saving">Save Changes</span>
          <span v-else class="flex items-center gap-2">
            <div class="spinner-small"></div>
            Saving...
          </span>
        </button>
        <button v-if="currentStep === 'review'" @click="proceedToFormat" class="btn btn-primary">
          Next: Select Format
        </button>
        <button v-if="currentStep === 'format'" @click="currentStep = 'review'" class="btn btn-secondary">
          Back
        </button>
        <button @click="$emit('close')" class="btn btn-secondary">
          {{ $t('common.cancel') }}
        </button>
      </div>
        </div>
      </div>
    </div>

    <!-- Seller Selection Modal -->
    <SelectSellerModal
      :show="showSellerModal"
      :company-id="props.companyId"
      @close="closeSellerModal"
      @select="handleSellerSelection"
    />

    <!-- Export Columns Modal -->
    <ExportColumnsModal
      :show="showExportColumnsModal"
      :company-id="props.companyId"
      :export-format="'excel'"
      @close="showExportColumnsModal = false"
      @saved="handleColumnsSaved"
    />

    <!-- Vendor Code Modal -->
    <VendorCodeModal
      v-if="showVendorCodeModal"
      :vendors="missingVendorCodes"
      @close="showVendorCodeModal = false"
      @saved="handleVendorCodesSaved"
    />

    <!-- Invoice Vendor Code Modal (for Business Navigator Counterparties) -->
    <InvoiceVendorCodeModal
      v-if="showInvoiceVendorCodeModal"
      :counterparties="missingInvoiceVendorCodes"
      @close="showInvoiceVendorCodeModal = false"
      @saved="handleInvoiceVendorCodesSaved"
    />

    <!-- Santa: lines that would go out with VAT -->
    <div v-if="showSantaVatModal" class="modal-backdrop" @click.self="closeSantaVatModal">
      <div class="modal-container santa-vat-modal">
        <div class="modal-content">
        <div class="modal-header">
          <div class="modal-title-section">
            <h2 class="modal-title">{{ $t('export.santaVatTitle', 'Редове със сума с ДДС') }}</h2>
          </div>
          <button class="btn btn-icon" @click="closeSantaVatModal">
            <LineIcon name="x" :size="15" :stroke-width="1.8" />
          </button>
        </div>

        <div class="modal-body">
          <p class="santa-vat-hint">
            {{ $t('export.santaVatHint', 'Santa очаква стойността на реда без ДДС. При тези редове документът не дава как да я определим — ще излязат както са отпечатани, тоест с ДДС. Отвори фактурата и попълни стойността без ДДС, или експортирай въпреки това.') }}
          </p>

          <div class="santa-vat-table" role="table">
            <div class="santa-vat-row santa-vat-row-head" role="row">
              <div class="sv-col-invoice">{{ $t('export.invoiceNumber', 'Номер') }}</div>
              <div class="sv-col-item">{{ $t('export.item', 'Артикул') }}</div>
              <div class="sv-col-qty">{{ $t('export.qty', 'Кол.') }}</div>
              <div class="sv-col-total">{{ $t('export.amount', 'Сума') }}</div>
            </div>
            <div v-for="(line, idx) in santaVatLines" :key="idx" class="santa-vat-row" role="row">
              <div class="sv-col-invoice">
                <router-link :to="`/scans/${line.invoice_id}`" target="_blank" class="sv-link">
                  {{ line.invoice_number || '—' }}
                </router-link>
                <span class="sv-seller">{{ line.seller_name }}</span>
              </div>
              <div class="sv-col-item">{{ line.description }}</div>
              <div class="sv-col-qty">{{ line.quantity }}</div>
              <div class="sv-col-total">{{ line.total }} {{ line.currency }}</div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeSantaVatModal">
            {{ $t('common.cancel', 'Отказ') }}
          </button>
          <button class="btn btn-primary" @click="exportDespiteVatWarning">
            {{ $t('export.exportAnyway', 'Експортирай въпреки това') }}
          </button>
        </div>
        </div>
      </div>
    </div>

    <!-- Santa: lines that will be booked as a service -->
    <div v-if="showSantaServiceModal" class="modal-backdrop" @click.self="closeSantaServiceModal">
      <div class="modal-container santa-vat-modal santa-service-modal">
        <div class="modal-content">
        <div class="modal-header">
          <div class="modal-title-section">
            <h2 class="modal-title">{{ $t('export.santaServiceTitle') }}</h2>
          </div>
          <button class="btn btn-icon" @click="closeSantaServiceModal">
            <LineIcon name="x" :size="15" :stroke-width="1.8" />
          </button>
        </div>

        <div class="modal-body">
          <p class="santa-vat-hint">{{ $t('export.santaServiceHint') }}</p>

          <div class="santa-vat-table" role="table">
            <div class="santa-vat-row santa-service-row santa-vat-row-head" role="row">
              <div class="sv-col-invoice">{{ $t('export.invoiceNumber', 'Номер') }}</div>
              <div class="sv-col-item">{{ $t('export.item', 'Артикул') }}</div>
              <div class="sv-col-code">{{ $t('export.accountCode') }}</div>
              <div class="sv-col-total">{{ $t('export.amount', 'Сума') }}</div>
            </div>
            <div v-for="(line, idx) in santaServiceLines" :key="idx" class="santa-vat-row santa-service-row" role="row">
              <div class="sv-col-invoice">
                <router-link :to="`/scans/${line.invoice_id}`" target="_blank" class="sv-link">
                  {{ line.invoice_number || '—' }}
                </router-link>
                <span class="sv-seller">{{ line.seller_name }}</span>
              </div>
              <div class="sv-col-item">{{ line.description }}</div>
              <div class="sv-col-code">
                <AccountCodeSelector
                  v-model="serviceCodeChoice[idx]"
                  :account-codes="serviceAccountCodes"
                />
                <span v-if="serviceCodeChoice[idx] !== line.account_code" class="sv-changed">
                  {{ line.account_code }} →
                </span>
              </div>
              <div class="sv-col-total">{{ line.total }} {{ line.currency }}</div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeSantaServiceModal">
            {{ $t('common.cancel', 'Отказ') }}
          </button>
          <button class="btn btn-primary" @click="confirmSantaServiceLines">
            {{ changedServiceCodes.length
              ? $t('export.santaServiceExportWithChanges', { count: changedServiceCodes.length })
              : $t('export.santaServiceConfirm') }}
          </button>
        </div>
        </div>
      </div>
    </div>

    <!-- Account Code Mapping Modal -->
    <MissingAccountCodesModal
      v-if="showAccountCodeModal"
      :items="missingAccountItems"
      :account-codes="availableAccountCodes"
      :company-id="props.companyId"
      @close="showAccountCodeModal = false"
      @saved="handleAccountCodesSaved"
      @account-code-created="handleAccountCodeCreated"
    />

    <!-- VAT Exemption Code Modal -->
    <VatExemptionCodeModal
      v-if="showVatExemptionModal"
      :invoices="invoicesNeedingVatCode"
      @close="showVatExemptionModal = false"
      @saved="handleVatCodesSaved"
    />

    <!-- Warning Confirmation Modal -->
    <div v-if="showWarningModal" class="modal-backdrop" @click.self="closeWarningModal">
      <div class="modal-container warning-confirmation-modal">
        <div class="modal-content">
          <div class="modal-header">
            <div class="modal-title-section">
              <h2 class="modal-title">Warning</h2>
            </div>
            <button @click="closeWarningModal" class="btn btn-icon">
              <LineIcon name="x" :size="15" :stroke-width="1.8" />
            </button>
          </div>
          <div class="modal-body">
            <div class="warning-confirmation-content">
              <p v-if="invalidVatCount > 0" class="warning-message warning-message-error">
                <strong>{{ invalidVatCount }} invoice{{ invalidVatCount > 1 ? 's' : '' }}</strong> have invalid VAT numbers
              </p>
              <p v-if="missingInfoInvoices.length > 0" class="warning-message warning-message-warning">
                <strong>{{ missingInfoInvoices.length }} invoice{{ missingInfoInvoices.length > 1 ? 's' : '' }}</strong> have missing information
              </p>
              <p v-if="vatConflictCount > 0" class="warning-message warning-message-warning">
                <strong>{{ vatConflictCount }} invoice{{ vatConflictCount > 1 ? 's' : '' }}</strong> have VAT conflicts
              </p>
              <p class="confirmation-question">Do you want to proceed anyway?</p>
            </div>
          </div>
          <div class="modal-footer">
            <button @click="confirmProceed" class="btn btn-primary">
              Yes, Proceed
            </button>
            <button @click="closeWarningModal" class="btn btn-secondary">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Accounting Date Confirmation Modal (for purchases Excel export) -->
    <div v-if="showAccountingDateModal" class="modal-backdrop" @click.self="closeAccountingDateModal">
      <div class="modal-container accounting-date-modal">
        <div class="modal-content">
          <div class="modal-header">
            <div class="modal-title-section">
              <h2 class="modal-title">{{ $t('export.accountingDateTitle', 'Дата на осчетоводяване') }}</h2>
            </div>
            <button @click="closeAccountingDateModal" class="btn btn-icon">
              <LineIcon name="x" :size="15" :stroke-width="1.8" />
            </button>
          </div>
          <div class="modal-body">
            <p class="accounting-date-hint">
              {{ $t('export.accountingDateHint', 'Избраната дата ще се приложи към всички фактури по-долу. При нужда можете ръчно да промените датата на отделна фактура.') }}
            </p>
            <div class="form-group">
              <label for="accountingDateInput">
                {{ $t('export.accountingDateLabel', 'Изберете дата на осчетоводяване за всички фактури') }}
              </label>
              <DateField v-model="accountingDate" class="form-control" />
            </div>

            <div v-if="accountingDateInvoiceList.length > 0" class="accounting-date-invoice-list">
              <div class="accounting-date-list-header">
                <strong>{{ $t('export.invoicesToExport', 'Фактури за експорт') }}</strong>
              </div>
              <div class="accounting-date-table" role="table">
                <div class="accounting-date-row accounting-date-row-head" role="row">
                  <div class="accounting-date-col-idx">#</div>
                  <div class="accounting-date-col-number">{{ $t('export.invoiceNumber', 'Номер') }}</div>
                  <div class="accounting-date-col-doc-date">{{ $t('export.docDate', 'Дата на документа') }}</div>
                  <div class="accounting-date-col-post-date">{{ $t('export.postDate', 'Дата на осчетоводяване') }}</div>
                </div>
                <div
                  v-for="(inv, idx) in accountingDateInvoiceList"
                  :key="`${inv.id}-${accountingDate}`"
                  class="accounting-date-row"
                  role="row"
                >
                  <div class="accounting-date-col-idx">{{ idx + 1 }}</div>
                  <div class="accounting-date-col-number">
                    <template v-if="inv.idOnly">#{{ inv.id }}</template>
                    <template v-else>{{ inv.number || $t('export.noNumber', 'Без номер') }}</template>
                  </div>
                  <div class="accounting-date-col-doc-date">
                    <span v-if="inv.date">{{ inv.date }}</span>
                    <span v-else class="accounting-date-col-muted">—</span>
                  </div>
                  <div class="accounting-date-col-post-date">
                    <DateField
                      v-model="displayDates[inv.id]"
                      class="accounting-date-row-input"
                      @change="onInvoiceDateChange(inv.id, $event)"
                    />
                    <button
                      v-if="invoiceAccountingDates[inv.id]"
                      type="button"
                      class="accounting-date-row-reset"
                      :title="$t('export.resetRow', 'Върни автоматичната стойност')"
                      @click="resetInvoiceDate(inv.id)"
                    >
                      <LineIcon name="rotate-left" :size="15" :stroke-width="1.8" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button @click="confirmAccountingDate" class="btn btn-primary" :disabled="!canConfirmAccountingDate">
              {{ $t('export.confirmAndExport', 'Потвърди и експортирай') }}
            </button>
            <button @click="closeAccountingDateModal" class="btn btn-secondary">
              {{ $t('common.cancel', 'Отказ') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- Accounting Date Settings Modal -->
  <Teleport to="body">
    <div v-if="showAccountingDateSettingsModal" class="modal-backdrop" @click.self="closeAccountingDateSettingsModal">
      <div class="modal-container" @click.stop>
        <div class="modal-content">
          <div class="modal-header">
            <div class="modal-title-section">
              <h2 class="modal-title">{{ $t('export.accountingDateSettings', 'Настройки на дата на осчетоводяване') }}</h2>
            </div>
            <button @click="closeAccountingDateSettingsModal" class="btn btn-icon">
              <LineIcon name="x" :size="15" :stroke-width="1.8" />
            </button>
          </div>
          <div class="modal-body">
            <p class="accounting-date-hint">
              {{ $t('export.accountingDateMonthsBackHint', 'Колко месеца назад да се приложи автоматично дата осчетоводяване? Фактури по-стари от този период ще запазят оригиналната си дата.') }}
            </p>
            <div class="form-group">
              <label for="accountingDateMonthsBack">{{ $t('export.monthsBack', 'Месеци назад') }}</label>
              <SelectField v-model="accountingDateMonthsBack" :options="monthsBackOptions" class="form-control" />
            </div>
          </div>
          <div class="modal-footer">
            <button @click="saveAccountingDateSettings" class="btn btn-primary">
              {{ $t('common.save', 'Запази') }}
            </button>
            <button @click="closeAccountingDateSettingsModal" class="btn btn-secondary">
              {{ $t('common.cancel', 'Отказ') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import DateField from '@/components/DateField.vue';
import SelectField from '@/components/SelectField.vue';
import LineIcon from '@/components/icons/LineIcon.vue';
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToast } from 'vue-toast-notification';
import { useAuthStore } from '@/stores/auth';
import api from '@/utils/api';
import SelectSellerModal from '@/components/modals/SelectSellerModal.vue';
import VendorCodeModal from './VendorCodeModal.vue';
import InvoiceVendorCodeModal from './InvoiceVendorCodeModal.vue';
import MissingAccountCodesModal from './MissingAccountCodesModal.vue';
import VatExemptionCodeModal from './VatExemptionCodeModal.vue';
import ExportColumnsModal from './ExportColumnsModal.vue';
import AccountCodeSelector from '@/components/selectors/AccountCodeSelector.vue';
import {
  hasMissingInfo as validateMissingInfo,
  getMissingInfoInvoices,
  detectVatConflicts,
  sortByMissingInfo,
  detectUniquePartnerNames
} from '@/services/exportValidationService';

const { t, locale } = useI18n();
const $toast = useToast();
const authStore = useAuthStore();

const props = defineProps({
  invoiceIds: {
    type: Array,
    default: null
  },
  invoiceId: {
    type: Number,
    default: null
  },
  companyName: {
    type: String,
    default: ''
  },
  companyId: {
    type: Number,
    default: null
  }
});

const emit = defineEmits(['close', 'exported', 'updated']);

const currentStep = ref('format'); // Skip review, go directly to format selection
const selectedFormat = ref('pdf');
const loading = ref(false);
const loadingInvoices = ref(false);
const saving = ref(false);
const error = ref(null);
const invoicesData = ref([]);
const originalInvoicesData = ref([]);
const showAjurSubmenu = ref(false);
const showNapSubmenu = ref(false);
const showExcelSubmenu = ref(false);
const showXeroSubmenu = ref(false);
const showQuickBooksSubmenu = ref(false);
const showTallySubmenu = ref(false);
const showOriginalFilesSubmenu = ref(false);
const showBusinessNavigatorSubmenu = ref(false);
const showSantaSubmenu = ref(false);
/* Whether a format's own list of options is open.
 *
 * The chevron used to ask this as one expression of nine comparisons written
 * inline in the template, repeated for the arrow and for the open class. */
const isSubmenuOpen = (value) => ({
    ajur: showAjurSubmenu,
    nap: showNapSubmenu,
    excel_export: showExcelSubmenu,
    xero: showXeroSubmenu,
    quickbooks: showQuickBooksSubmenu,
    tallyprime: showTallySubmenu,
    original_files: showOriginalFilesSubmenu,
    business_navigator: showBusinessNavigatorSubmenu,
    santa: showSantaSubmenu,
}[value]?.value === true);

const excelTypeFilter = ref('all'); // 'all', 'purchase', 'sale'
const showExcelTypeOptions = ref(false);
const showExcelDetailedTypeOptions = ref(false);

// Seller selection modal
const showSellerModal = ref(false);
const currentInvoiceId = ref(null);

// Warning confirmation modal
const showWarningModal = ref(false);

// Accounting-date confirmation modal (for Excel exports)
const showAccountingDateModal = ref(false);
const accountingDate = ref('');
const accountingDateConfirmed = ref(false);
// Per-invoice overrides — { [invoiceId]: 'YYYY-MM-DD' }; a row appears here only when the user manually changes it.
const invoiceAccountingDates = ref({});

// Accounting date settings modal
const showAccountingDateSettingsModal = ref(false);
const accountingDateMonthsBack = ref(12); // Default: 12 months back

// Resolve the picker value for one row:
//   manual override > global accounting date > invoice issue date
const resolveRowDate = (inv) => {
  const override = invoiceAccountingDates.value[inv.id];
  if (override) return override;
  if (accountingDate.value) return accountingDate.value;
  return inv.date || '';
};

const onInvoiceDateChange = (invoiceId, value) => {
  if (!value) {
    // User cleared it — drop the override and fall back to global date / issue date.
    const next = { ...invoiceAccountingDates.value };
    delete next[invoiceId];
    invoiceAccountingDates.value = next;
    const inv = accountingDateInvoiceList.value.find(i => i.id === invoiceId);
    if (inv) {
      displayDates.value[invoiceId] = resolveRowDate(inv);
    }
    return;
  }
  invoiceAccountingDates.value = { ...invoiceAccountingDates.value, [invoiceId]: value };
  displayDates.value[invoiceId] = value;
};

const resetInvoiceDate = (invoiceId) => {
  const next = { ...invoiceAccountingDates.value };
  delete next[invoiceId];
  invoiceAccountingDates.value = next;
  const inv = accountingDateInvoiceList.value.find(i => i.id === invoiceId);
  if (inv) {
    displayDates.value[invoiceId] = resolveRowDate(inv);
  }
};

// Reactive object to store current display dates for each invoice
const displayDates = ref({});

// Build a preview list of the invoices in scope, with the posting-date we'd apply to each.
// Cutoff from the "Months back" setting.
//  - 0  → "Outside the current month": boundary is the 1st of THIS month, so every
//         invoice dated in a previous month qualifies regardless of its day (a 30 Jun
//         and a 25 Jun invoice are treated the same). Fixes the mid-month split of a
//         rolling window.
//  - N>0 → rolling window: today − N months.
const monthsBackCutoff = () => {
  const monthsBack = authStore.accountingDateMonthsBack ?? 12; // 0 is valid, keep it
  const cutoff = new Date();
  if (Number(monthsBack) === 0) {
    cutoff.setDate(1);
  } else {
    cutoff.setMonth(cutoff.getMonth() - Number(monthsBack));
  }
  cutoff.setHours(0, 0, 0, 0);
  return cutoff;
};

const accountingDateInvoiceList = computed(() => {
  const chosen = accountingDate.value ? new Date(accountingDate.value + 'T00:00:00') : null;
  // Cutoff: when the user has picked an accounting date, anything older than
  // (chosen − 1 month) qualifies for manual posting. Otherwise, fall back to
  // the user's "Months back" setting from today — same window that decides
  // whether the modal opens at all, so the trigger and the listed rows agree.
  let cutoff;
  if (chosen) {
    cutoff = new Date(chosen);
    cutoff.setMonth(cutoff.getMonth() - 1);
  } else {
    cutoff = monthsBackCutoff();
  }

  // Prefer invoice_date (the document date shown in the list) over issue_date, so the
  // accounting-date window matches the date the user sees. Some invoices have an
  // invoice_date in the previous month but an issue_date in the current one; the list
  // shows invoice_date, so filter by it.
  const issueDateOf = (inv) =>
    inv?.original_result?.invoice_date
    || inv?.result?.invoice_date
    || inv?.invoice_date
    || inv?.original_result?.issue_date
    || inv?.result?.issue_date
    || inv?.issue_date
    || '';

  const olderThanCutoff = (inv) => {
    const raw = issueDateOf(inv);
    if (!raw) return false;
    const d = new Date(String(raw).length === 10 ? raw + 'T00:00:00' : raw);
    if (isNaN(d)) return false;
    return d < cutoff;
  };

  const mapInvoice = (inv, idx) => ({
    id: inv?.id ?? idx,
    number: inv?.invoice_number || '',
    date: issueDateOf(inv) || '',
    mapped: chosen ? accountingDate.value : null,
  });

  // Prefer loaded invoice details; otherwise fall back to whatever IDs we were passed in props.
  const loaded = invoicesData.value || [];
  if (loaded.length > 0) {
    let filtered = loaded;

    // Filter by invoice type (purchase/sale)
    if (excelTypeFilter.value === 'purchase') {
      filtered = filtered.filter(inv => {
        if (inv.type === 'expense') return true;
        const buyerName = inv.buyer_name || inv.original_result?.buyer_name || '';
        const companyName = props.companyName || '';
        return companyName && buyerName.toLowerCase().includes(companyName.toLowerCase());
      });
    } else if (excelTypeFilter.value === 'sale') {
      filtered = filtered.filter(inv => {
        if (inv.type === 'income') return true;
        const sellerName = inv.seller_name || inv.original_result?.seller_name || '';
        const companyName = props.companyName || '';
        return companyName && sellerName.toLowerCase().includes(companyName.toLowerCase());
      });
    }

    // Show only invoices older than one month from the chosen accounting date.
    filtered = filtered.filter(olderThanCutoff);

    return filtered.map(mapInvoice);
  }

  const propIds = props.invoiceId ? [props.invoiceId] : (props.invoiceIds || []);
  return propIds.filter(Boolean).map((id, i) => ({
    id,
    number: '',
    date: '',
    mapped: null,
    idOnly: true
  }));
});

// When the user changes the global accounting date, cascade it to every row that
// hasn't been manually overridden.
watch(accountingDate, () => {
  const newDisplayDates = {};
  for (const inv of accountingDateInvoiceList.value) {
    newDisplayDates[inv.id] = resolveRowDate(inv);
  }
  displayDates.value = newDisplayDates;
});

// Initialise displayDates when the invoice list first loads (or changes).
watch(accountingDateInvoiceList, (newList) => {
  const newDisplayDates = {};
  for (const inv of newList) {
    newDisplayDates[inv.id] = resolveRowDate(inv);
  }
  displayDates.value = newDisplayDates;
}, { immediate: true });

const closeAccountingDateModal = () => {
  showAccountingDateModal.value = false;
  accountingDate.value = '';
  accountingDateConfirmed.value = false;
  invoiceAccountingDates.value = {};
  loading.value = false;
};

// Confirm is enabled when every row in the preview has a resolved date
// (each row defaults to its own invoice/document date, so this is normally true).
const canConfirmAccountingDate = computed(() => {
  const list = accountingDateInvoiceList.value;
  if (list.length === 0) return false;
  return list.every(inv => !!displayDates.value[inv.id]);
});

const confirmAccountingDate = async () => {
  if (!canConfirmAccountingDate.value) return;
  // Persist each row's resolved date (override > global > invoice's own date)
  // so the export payload sends one accounting date per invoice.
  const dates = {};
  for (const inv of accountingDateInvoiceList.value) {
    const v = displayDates.value[inv.id] || inv.date || '';
    if (v) dates[inv.id] = v;
  }
  invoiceAccountingDates.value = dates;

  showAccountingDateModal.value = false;
  accountingDateConfirmed.value = true;
  await handleExport();
};

const openAccountingDateSettingsModal = () => {
  // Load current setting from auth store
  accountingDateMonthsBack.value = authStore.accountingDateMonthsBack ?? 12;
  showAccountingDateSettingsModal.value = true;
};

const closeAccountingDateSettingsModal = () => {
  showAccountingDateSettingsModal.value = false;
};

const saveAccountingDateSettings = async () => {
  try {
    const result = await authStore.updateSettings({
      accounting_date_months_back: accountingDateMonthsBack.value
    });
    
    if (result.success) {
      $toast.success('Настройките са запазени успешно');
      showAccountingDateSettingsModal.value = false;
    } else {
      throw new Error(result.error);
    }
  } catch (error) {
    console.error('Failed to save accounting date settings:', error);
    $toast.error('Грешка при запазване на настройките');
  }
};

// True when at least one invoice in the export is older than the user's
// "Months back" setting. The accounting-date confirmation modal should only
// appear in that case — recent-only batches export with each invoice's own
// date and skip the prompt.
const hasInvoiceOlderThanMonthsBack = () => {
  const cutoff = monthsBackCutoff();

  const list = accountingDateInvoiceList.value;
  if (!list.length) return false;

  return list.some(inv => {
    if (!inv.date) return true;
    const d = new Date(String(inv.date).length === 10 ? inv.date + 'T00:00:00' : inv.date);
    if (isNaN(d)) return true;
    return d < cutoff;
  });
};

// Vendor code modal
const showVendorCodeModal = ref(false);
const missingVendorCodes = ref([]);

// Invoice vendor code modal (for Business Navigator counterparties)
const showInvoiceVendorCodeModal = ref(false);
const missingInvoiceVendorCodes = ref([]);

// Account code mapping modal
const showAccountCodeModal = ref(false);

// Santa: lines whose value cannot be established without VAT.
const showSantaVatModal = ref(false);
const santaVatLines = ref([]);
// Set once the accountant chooses to export anyway, so the warning does not
// reappear on the retry it triggers.
const santaVatWarningAcknowledged = ref(false);

const closeSantaVatModal = () => {
  showSantaVatModal.value = false;
};

const exportDespiteVatWarning = async () => {
  santaVatWarningAcknowledged.value = true;
  showSantaVatModal.value = false;
  await handleExport();
};

// Santa: lines the mapping booked as a service (internal code U). Shown for
// confirmation, not correction — the codes are exported exactly as mapped.
const showSantaServiceModal = ref(false);
const santaServiceLines = ref([]);
// Set once confirmed, so the retry it triggers does not ask again.
const santaServiceConfirmed = ref(false);

// The account codes to pick from, and the choice made per line. Index-aligned
// with santaServiceLines, which is built once when the modal opens.
const serviceAccountCodes = ref([]);
const serviceCodeChoice = ref([]);

const changedServiceCodes = computed(() =>
  santaServiceLines.value
    .map((line, idx) => ({ line, code: serviceCodeChoice.value[idx] }))
    .filter(({ line, code }) => code && code !== line.account_code)
);

const closeSantaServiceModal = () => {
  showSantaServiceModal.value = false;
};

/**
 * Open the modal with the lines the check returned, each row preset to the code
 * the mapping chose, so leaving everything alone confirms what would have been
 * exported anyway.
 */
const openSantaServiceModal = async (lines) => {
  santaServiceLines.value = lines;
  serviceCodeChoice.value = lines.map(line => line.account_code);

  try {
    const { data } = await api.get(`/api/companies/${props.companyId}/account-codes`);
    serviceAccountCodes.value = data.data || [];
  } catch (err) {
    console.error('Failed to load account codes for service lines:', err);
    serviceAccountCodes.value = [];
  }

  showSantaServiceModal.value = true;
};

/**
 * Codes chosen by hand, keyed "invoiceId|lineIndex" for the export request.
 *
 * Carried with the file and nowhere else. A correction made here must not
 * rewrite the company's mappings: those are built up over years, and the next
 * invoice with the same description still maps where it always did.
 */
const santaServiceOverrides = computed(() => {
  const overrides = {};

  for (const { line, code } of changedServiceCodes.value) {
    const target = serviceAccountCodes.value.find(ac => ac.account_code === code);
    if (target) overrides[`${line.invoice_id}|${line.line_index}`] = target.id;
  }

  return overrides;
});

const confirmSantaServiceLines = async () => {
  santaServiceConfirmed.value = true;
  showSantaServiceModal.value = false;
  await handleExport();
};
const missingAccountItems = ref([]);
const availableAccountCodes = ref([]);

// Export columns modal
const showExportColumnsModal = ref(false);

// VAT exemption code modal for Ajur export
const showVatExemptionModal = ref(false);
const invoicesNeedingVatCode = ref([]);

const invoiceCount = computed(() => {
  if (props.invoiceId) return 1;
  return props.invoiceIds?.length || 0;
});

const exportInfo = computed(() => {
  const countText = invoiceCount.value === 1
    ? t('export.exportCount', { count: invoiceCount.value })
    : t('export.exportCountPlural', { count: invoiceCount.value });
  return `${countText} ${t('export.selectFormat')}`;
});

const hasUnsavedChanges = computed(() => {
  return JSON.stringify(invoicesData.value) !== JSON.stringify(originalInvoicesData.value);
});

const missingInfoInvoices = computed(() => {
  return getMissingInfoInvoices(invoicesData.value);
});

const sortedInvoicesData = computed(() => {
  // Sort invoices: invalid VATs first, then warnings, then by missing info
  return [...invoicesData.value].sort((a, b) => {
    const aInvalidVat = getSellerVatValidation(a) === false || getBuyerVatValidation(a) === false;
    const bInvalidVat = getSellerVatValidation(b) === false || getBuyerVatValidation(b) === false;
    
    // Invalid VAT invoices first
    if (aInvalidVat && !bInvalidVat) return -1;
    if (!aInvalidVat && bInvalidVat) return 1;
    
    const aHasWarning = hasVatConflict(a.id);
    const bHasWarning = hasVatConflict(b.id);
    
    // Warning invoices second
    if (aHasWarning && !bHasWarning) return -1;
    if (!aHasWarning && bHasWarning) return 1;
    
    // Then sort by missing info
    const aMissing = validateMissingInfo(a);
    const bMissing = validateMissingInfo(b);
    
    if (aMissing && !bMissing) return -1;
    if (!aMissing && bMissing) return 1;
    
    return 0;
  });
});

const hasMissingInfo = (invoice) => {
  return validateMissingInfo(invoice);
};

// Detect duplicate VAT numbers with different names
const vendorVatConflicts = computed(() => {
  return detectVatConflicts(invoicesData.value);
});

const hasVatConflict = (invoiceId) => {
  return vendorVatConflicts.value.has(invoiceId);
};

// VAT conflict warning
const vatConflictCount = computed(() => {
  return vendorVatConflicts.value.size;
});

const vatConflictInvoices = computed(() => {
  return invoicesData.value.filter(invoice => vendorVatConflicts.value.has(invoice.id));
});

// Detect unique partner names when there are 3+ different names
const uniquePartnerNames = computed(() => {
  return detectUniquePartnerNames(invoicesData.value);
});

const hasUniqueName = (invoiceId) => {
  return uniquePartnerNames.value.has(invoiceId);
};

// Invalid VAT count
const invalidVatCount = computed(() => {
  return invoicesData.value.filter(invoice => 
    getSellerVatValidation(invoice) === false || getBuyerVatValidation(invoice) === false
  ).length;
});

// VAT validation functions
const getSellerVatValidation = (invoice) => {
  const original = invoice.original_result || {};
  
  // Check root level first
  if ('seller_vat_number_valid' in invoice) {
    return invoice.seller_vat_number_valid;
  }
  
  // Check for inverse field
  if ('seller_vat_number_invalid' in invoice) {
    return !invoice.seller_vat_number_invalid;
  }
  
  // Check in original_result
  if ('seller_vat_number_valid' in original) {
    return original.seller_vat_number_valid;
  }
  
  if ('seller_vat_number_invalid' in original) {
    return !original.seller_vat_number_invalid;
  }
  
  return null;
};

const getBuyerVatValidation = (invoice) => {
  const original = invoice.original_result || {};
  
  // Check root level first
  if ('buyer_vat_number_valid' in invoice) {
    return invoice.buyer_vat_number_valid;
  }
  
  // Check for inverse field
  if ('buyer_vat_number_invalid' in invoice) {
    return !invoice.buyer_vat_number_invalid;
  }
  
  // Check in original_result
  if ('buyer_vat_number_valid' in original) {
    return original.buyer_vat_number_valid;
  }
  
  if ('buyer_vat_number_invalid' in original) {
    return !original.buyer_vat_number_invalid;
  }
  
  return null;
};

const formats = computed(() => [
  {
    value: 'original_files',
    name: 'Original Files',
    description: 'Export original invoice files',
    icon: 'file-text',
    submenu: [
      {
        value: 'original_jpg',
        name: 'Original as JPG',
        description: 'Download original files as JPG images',
        icon: 'file-text'
      },
      {
        value: 'original_pdf',
        name: 'Original as PDF',
        description: 'Download original files as PDF',
        icon: 'file-text'
      },
      {
        value: 'html',
        name: 'HTML',
        description: 'HTML files in a ZIP archive',
        icon: 'file-text'
      }
    ]
  },
  {
    value: 'excel_export',
    name: t('export.formatExcelExport'),
    description: t('export.formatExcelExportDesc'),
    icon: 'file-spreadsheet',
    submenu: [
      {
        value: 'excel',
        name: t('export.formatExcel'),
        description: t('export.formatExcelDesc'),
        icon: 'table'
      },
      {
        value: 'excel_detailed',
        name: t('export.formatExcelDetailed'),
        description: t('export.formatExcelDetailedDesc'),
        icon: 'table'
      },
      {
        value: 'docdb',
        name: t('export.formatDocDB'),
        description: t('export.formatDocDBDesc'),
        icon: 'database'
      }
    ]
  },
  {
    value: 'invoicescan_xml',
    name: 'InvoiceScan XML',
    description: 'Single XML file with all invoices wrapped in Documents/Document tags',
    icon: 'file-text'
  },
  {
    value: 'nap',
    name: t('export.formatNap'),
    description: t('export.formatNapDesc'),
    icon: 'receipt',
    submenu: [
      {
        value: 'txt_pokupki',
        name: t('export.formatTxtPokupki'),
        description: t('export.formatTxtPokupkiDesc'),
        icon: 'file-text'
      },
      {
        value: 'txt_prodagbi',
        name: t('export.formatTxtProdagbi'),
        description: t('export.formatTxtProdagbiDesc'),
        icon: 'file-text'
      }
    ]
  },
  {
    value: 'microinvest_xml',
    name: t('export.formatMicroInvest'),
    description: t('export.formatMicroInvestDesc'),
    icon: 'file-text',
    beta: true
  },
  {
    value: 'konto_xlsx',
    name: t('export.formatKonto'),
    description: t('export.formatKontoDesc'),
    icon: 'file-spreadsheet',
    beta: true
  },
  {
    value: 'ajur',
    name: t('export.formatAjur'),
    description: t('export.formatAjurDesc'),
    icon: 'layers',
    beta: true,
    submenu: [
      {
        value: 'ajur_purchase',
        name: t('export.formatAjurPurchase'),
        description: t('export.formatAjurPurchaseDesc'),
        icon: 'receipt'
      },
      {
        value: 'ajur_sales',
        name: t('export.formatAjurSales'),
        description: t('export.formatAjurSalesDesc'),
        icon: 'receipt'
      }
    ]
  },
  {
    value: 'plusminus_csv',
    name: t('export.formatPlusMinus'),
    description: t('export.formatPlusMinusDesc'),
    icon: 'file-spreadsheet',
    beta: true
  },
  {
    value: 'xero',
    name: 'Xero',
    description: 'Choose between CSV or Excel format for Xero accounting software',
    icon: 'receipt',
    submenu: [
      {
        value: 'xero_csv',
        name: 'Xero CSV',
        description: 'CSV format for Xero import',
        icon: 'file-spreadsheet'
      },
      {
        value: 'xero_xlsx',
        name: 'Xero XLSX',
        description: 'Excel format for Xero import',
        icon: 'file-spreadsheet'
      }
    ]
  },
  {
    value: 'quickbooks',
    name: 'QuickBooks',
    description: 'Choose between CSV or Excel format for QuickBooks Online import',
    icon: 'receipt',
    submenu: [
      {
        value: 'quickbooks_csv',
        name: 'QuickBooks CSV',
        description: 'CSV format for QuickBooks Online import',
        icon: 'file-spreadsheet'
      },
      {
        value: 'quickbooks_xlsx',
        name: 'QuickBooks XLSX',
        description: 'Excel format for QuickBooks Online import',
        icon: 'file-spreadsheet'
      }
    ]
  },
  /*{
    value: 'tallyprime',
    name: 'TallyPrime',
    description: 'Export formats for TallyPrime accounting software',
    icon: 'calculator',
    submenu: [
      {
        value: 'tally',
        name: 'TallyPrime XML',
        description: 'XML voucher format for direct TallyPrime import',
        icon: 'file-text'
      },
      {
        value: 'tally_excel',
        name: 'TallyPrime Excel',
        description: 'Excel format for TallyPrime Excel import (easier to review)',
        icon: 'file-spreadsheet'
      },
      {
        value: 'tally_csv',
        name: 'TallyPrime CSV',
        description: 'CSV format for TallyPrime import',
        icon: 'file-spreadsheet'
      }
    ]
  },*/
  {
    value: 'business_navigator',
    name: 'Business Navigator',
    description: 'Export invoices and counterparties for Business Navigator accounting software',
    icon: 'building',
    submenu: [
      {
        value: 'business_navigator_purchases',
        name: 'Purchase Invoices',
        description: 'Export purchase invoices only (expenses)',
        icon: 'file-import'
      },
      {
        value: 'business_navigator_sales',
        name: 'Sale Invoices',
        description: 'Export sale invoices only (income)',
        icon: 'download'
      },
      {
        value: 'business_navigator_counterparties',
        name: 'Counterparties',
        description: 'Export clients and suppliers',
        icon: 'users'
      }
    ]
  },
  {
    value: 'preciz_cx',
    name: 'Preciz-CX',
    description: 'Export for КомБайт Прециз-СХ / Склад-СХ accounting (VHODPRCN.TXT + VHODPRC.TXT)',
    icon: 'banknote',
    beta: true,
  },
  {
    value: 'santa',
    name: 'Santa',
    description: 'Export format for Santa accounting software',
    icon: 'calculator',
    beta: true,
    submenu: [
      {
        value: 'santa_purchases',
        name: 'Santa Purchases',
        description: 'Export purchase invoices for Santa accounting',
        icon: 'shopping-cart'
      },
      {
        value: 'santa_sales',
        name: 'Santa Sales',
        description: 'Export sales invoices for Santa accounting',
        icon: 'banknote'
      }
    ]
  }
]);

const formatDate = (date) => {
  if (!date) return 'N/A';
  const d = new Date(date);
  return d.toLocaleDateString('en-GB');
};

const formatAmount = (amount) => {
  if (amount == null) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

const viewInvoice = (invoiceId) => {
  // Open invoice detail page in new tab
  window.open(`/scans/${invoiceId}`, '_blank');
};

const loadInvoicesData = async () => {
  loadingInvoices.value = true;
  error.value = null;

  try {
    let invoiceIds = props.invoiceId ? [props.invoiceId] : props.invoiceIds;
    invoiceIds = (invoiceIds || []).filter(id => id != null);

    if (invoiceIds.length === 0) {
      error.value = 'No invoices selected';
      return;
    }

    const response = await api.post('/api/scans/details', {
      invoice_ids: invoiceIds
    }, {
      params: {
        _t: Date.now() // Cache-busting parameter to ensure fresh data
      }
    });

    // Log the raw response for debugging
    console.log('Raw API response:', response.data);
    
    // Convert 'N/A' values to empty strings for better editing experience
    // Prefer values from original_result over flattened fields for accuracy
    invoicesData.value = (response.data.invoices || []).map(invoice => {
      const original = invoice.original_result || {};
      return {
        ...invoice,
        invoice_number: (original.invoice_number || invoice.invoice_number) === 'N/A' ? '' : (original.invoice_number || invoice.invoice_number),
        invoice_date: (original.invoice_date || invoice.invoice_date) === 'N/A' ? '' : (original.invoice_date || invoice.invoice_date),
        company_vat: (original.buyer_vat_number || invoice.company_vat) === 'N/A' ? '' : (original.buyer_vat_number || invoice.company_vat),
        vendor_vat: (original.seller_vat_number || invoice.vendor_vat) === 'N/A' ? '' : (original.seller_vat_number || invoice.vendor_vat),
        vendor_name: (original.seller_name || invoice.vendor_name) === 'N/A' ? '' : (original.seller_name || invoice.vendor_name),
        currency: (original.currency || invoice.currency) === 'N/A' ? '' : (original.currency || invoice.currency || 'BGN'),
        vat_amount: (original.tax_amount || invoice.vat_amount) === 'N/A' ? '' : (original.tax_amount || invoice.vat_amount),
        total_amount: (original.grand_total || invoice.total_amount) === 'N/A' ? '' : (original.grand_total || invoice.total_amount),
        document_type: (original.invoice_type || invoice.document_type) === 'N/A' ? '' : (original.invoice_type || invoice.document_type)
      };
    });
    
    console.log('Processed invoicesData:', invoicesData.value);
    
    // Store original data for change tracking
    originalInvoicesData.value = JSON.parse(JSON.stringify(invoicesData.value));
  } catch (err) {
    console.error('Failed to load invoice details:', err);
    error.value = 'Failed to load invoice details';
  } finally {
    loadingInvoices.value = false;
  }
};

onMounted(() => {
  loadInvoicesData();
});

// Log VAT conflicts for debugging
watch(vendorVatConflicts, (conflicts) => {
  console.log('VAT conflicts detected:', conflicts);
  console.log('Conflicting invoice IDs:', Array.from(conflicts));
  if (conflicts.size > 0) {
    console.log('Conflicting invoices details:', vatConflictInvoices.value);
  }
});

const saveChanges = async () => {
  saving.value = true;
  error.value = null;
  
  try {
    console.log('Starting save process...');
    console.log('Current invoicesData:', invoicesData.value);
    console.log('Original invoicesData:', originalInvoicesData.value);
    
    // Save each modified invoice
    const savePromises = invoicesData.value.map(async (invoice) => {
      const original = originalInvoicesData.value.find(o => o.id === invoice.id);
      
      // Check if this invoice was modified
      const invoiceStr = JSON.stringify(invoice);
      const originalStr = JSON.stringify(original);
      const hasChanges = invoiceStr !== originalStr;
      
      console.log(`Invoice ${invoice.id} - Has changes:`, hasChanges);
      
      if (hasChanges) {
        // Get the complete original result object
        const completeResult = { ...(invoice.original_result || {}) };
        
        console.log('Complete original result for invoice:', invoice.id, completeResult);
        
        // Update only the specific fields that exist in original or add them
        // Invoice number
        if (invoice.invoice_number !== '') {
          completeResult.invoice_number = invoice.invoice_number;
          if ('InvoiceNumber' in completeResult) completeResult.InvoiceNumber = invoice.invoice_number;
        }
        
        // Invoice date
        if (invoice.invoice_date !== '') {
          completeResult.invoice_date = invoice.invoice_date;
          if ('InvoiceDate' in completeResult) completeResult.InvoiceDate = invoice.invoice_date;
        }
        
        // Company VAT
        if (invoice.company_vat !== '') {
          completeResult.company_vat = invoice.company_vat;
          if ('CompanyVat' in completeResult) completeResult.CompanyVat = invoice.company_vat;
          if ('buyer_tax_id' in completeResult) completeResult.buyer_tax_id = invoice.company_vat;
          if ('buyer_vat_number' in completeResult) completeResult.buyer_vat_number = invoice.company_vat;
        }
        
        // Vendor VAT
        if (invoice.vendor_vat !== '') {
          completeResult.seller_vat_number = invoice.vendor_vat;
          if ('vendor_vat' in completeResult) completeResult.vendor_vat = invoice.vendor_vat;
          if ('VendorVat' in completeResult) completeResult.VendorVat = invoice.vendor_vat;
          if ('seller_tax_id' in completeResult) completeResult.seller_tax_id = invoice.vendor_vat;
        }
        
        // Vendor name
        if (invoice.vendor_name !== '') {
          completeResult.seller_name = invoice.vendor_name;
          if ('vendor_name' in completeResult) completeResult.vendor_name = invoice.vendor_name;
          if ('VendorName' in completeResult) completeResult.VendorName = invoice.vendor_name;
        }
        
        // Currency
        if (invoice.currency !== '') {
          completeResult.currency = invoice.currency;
          if ('Currency' in completeResult) completeResult.Currency = invoice.currency;
          if ('currency_code' in completeResult) completeResult.currency_code = invoice.currency;
          if ('CurrencyCode' in completeResult) completeResult.CurrencyCode = invoice.currency;
        }
        
        // VAT amount
        if (invoice.vat_amount !== '') {
          completeResult.tax_amount = invoice.vat_amount;
          if ('vat_amount' in completeResult) completeResult.vat_amount = invoice.vat_amount;
          if ('TaxAmount' in completeResult) completeResult.TaxAmount = invoice.vat_amount;
          if ('VatAmount' in completeResult) completeResult.VatAmount = invoice.vat_amount;
        }
        
        // Total amount
        if (invoice.total_amount !== '') {
          completeResult.total_amount = invoice.total_amount;
          if ('grand_total' in completeResult) completeResult.grand_total = invoice.total_amount;
          if ('TotalAmount' in completeResult) completeResult.TotalAmount = invoice.total_amount;
          if ('Total' in completeResult) completeResult.Total = invoice.total_amount;
        }
        
        // Document type
        if (invoice.document_type !== '') {
          completeResult.document_type = invoice.document_type;
          if ('DocumentType' in completeResult) completeResult.DocumentType = invoice.document_type;
        }
        
        const updateData = {
          result: completeResult
        };
        
        console.log('Updating invoice:', invoice.id, 'with complete result:', updateData);
        
        const response = await api.put(`/api/invoices/${invoice.id}`, updateData);
        console.log('Update response:', response);
        return response;
      }
    });
    
    const results = await Promise.all(savePromises.filter(p => p !== undefined));
    console.log('All saves completed:', results.length, 'invoices updated');
    
    // Delay to ensure backend has processed the updates and cache is cleared
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Reload invoice data from backend to get the updated values
    console.log('Reloading invoice data...');
    await loadInvoicesData();
    
    // Wait for Vue to update the DOM
    await nextTick();
    console.log('Data reloaded and DOM updated');
    
    // Emit updated event so parent can reload data
    emit('updated');
    
    // Show success message
    $toast.success('Changes saved successfully!', {
      position: 'top-right',
      duration: 3000
    });
  } catch (err) {
    console.error('Failed to save changes:', err);
    error.value = 'Failed to save changes. Please try again.';
    $toast.error('Failed to save changes. Please try again.', {
      position: 'top-right',
      duration: 5000
    });
  } finally {
    saving.value = false;
  }
};

const proceedToFormat = async () => {
  // Check if there are any warnings
  const hasWarnings = missingInfoInvoices.value.length > 0 || vatConflictCount.value > 0 || invalidVatCount.value > 0;
  
  if (hasWarnings) {
    // Show warning modal instead of using confirm()
    showWarningModal.value = true;
    return;
  }
  
  // If there are unsaved changes, save them first
  if (hasUnsavedChanges.value) {
    await saveChanges();
  }
  currentStep.value = 'format';
};

const closeWarningModal = () => {
  showWarningModal.value = false;
};

const handleColumnsSaved = (columns) => {
  console.log('Export columns saved:', columns);
  // Columns are saved server-side, just close the modal
  // They'll be applied automatically on next export
};

const openColumnsModal = () => {
  if (!showExcelSubmenu.value) {
    return;
  }

  showExportColumnsModal.value = true;
};

const confirmProceed = async () => {
  showWarningModal.value = false;
  
  // If there are unsaved changes, save them first
  if (hasUnsavedChanges.value) {
    await saveChanges();
  }
  currentStep.value = 'format';
};

const handleFormatClick = (format) => {
  if (format.submenu) {
    // Toggle submenu for formats with submenus
    if (format.value === 'ajur') {
      showAjurSubmenu.value = !showAjurSubmenu.value;
      showNapSubmenu.value = false;
      showExcelSubmenu.value = false;
      showXeroSubmenu.value = false;
      showQuickBooksSubmenu.value = false;
      showTallySubmenu.value = false;
      showOriginalFilesSubmenu.value = false;
      showBusinessNavigatorSubmenu.value = false;
      showSantaSubmenu.value = false;
    } else if (format.value === 'original_files') {
      showOriginalFilesSubmenu.value = !showOriginalFilesSubmenu.value;
      showAjurSubmenu.value = false;
      showNapSubmenu.value = false;
      showExcelSubmenu.value = false;
      showXeroSubmenu.value = false;
      showQuickBooksSubmenu.value = false;
      showTallySubmenu.value = false;
      showBusinessNavigatorSubmenu.value = false;
      showSantaSubmenu.value = false;
    } else if (format.value === 'nap') {
      showNapSubmenu.value = !showNapSubmenu.value;
      showAjurSubmenu.value = false;
      showExcelSubmenu.value = false;
      showXeroSubmenu.value = false;
      showQuickBooksSubmenu.value = false;
      showTallySubmenu.value = false;
      showOriginalFilesSubmenu.value = false;
      showBusinessNavigatorSubmenu.value = false;
      showSantaSubmenu.value = false;
    } else if (format.value === 'excel_export') {
      showExcelSubmenu.value = !showExcelSubmenu.value;
      showAjurSubmenu.value = false;
      showNapSubmenu.value = false;
      showXeroSubmenu.value = false;
      showQuickBooksSubmenu.value = false;
      showTallySubmenu.value = false;
      showOriginalFilesSubmenu.value = false;
      showBusinessNavigatorSubmenu.value = false;
      showSantaSubmenu.value = false;
    } else if (format.value === 'xero') {
      showXeroSubmenu.value = !showXeroSubmenu.value;
      showAjurSubmenu.value = false;
      showNapSubmenu.value = false;
      showExcelSubmenu.value = false;
      showQuickBooksSubmenu.value = false;
      showTallySubmenu.value = false;
      showOriginalFilesSubmenu.value = false;
      showBusinessNavigatorSubmenu.value = false;
      showSantaSubmenu.value = false;
    } else if (format.value === 'quickbooks') {
      showQuickBooksSubmenu.value = !showQuickBooksSubmenu.value;
      showAjurSubmenu.value = false;
      showNapSubmenu.value = false;
      showExcelSubmenu.value = false;
      showXeroSubmenu.value = false;
      showTallySubmenu.value = false;
      showOriginalFilesSubmenu.value = false;
      showBusinessNavigatorSubmenu.value = false;
      showSantaSubmenu.value = false;
    } else if (format.value === 'tallyprime') {
      showTallySubmenu.value = !showTallySubmenu.value;
      showAjurSubmenu.value = false;
      showNapSubmenu.value = false;
      showExcelSubmenu.value = false;
      showXeroSubmenu.value = false;
      showQuickBooksSubmenu.value = false;
      showOriginalFilesSubmenu.value = false;
      showBusinessNavigatorSubmenu.value = false;
      showSantaSubmenu.value = false;
    } else if (format.value === 'business_navigator') {
      showBusinessNavigatorSubmenu.value = !showBusinessNavigatorSubmenu.value;
      showAjurSubmenu.value = false;
      showNapSubmenu.value = false;
      showExcelSubmenu.value = false;
      showXeroSubmenu.value = false;
      showQuickBooksSubmenu.value = false;
      showTallySubmenu.value = false;
      showOriginalFilesSubmenu.value = false;
      showSantaSubmenu.value = false;
    } else if (format.value === 'santa') {
      showSantaSubmenu.value = !showSantaSubmenu.value;
      showAjurSubmenu.value = false;
      showNapSubmenu.value = false;
      showExcelSubmenu.value = false;
      showXeroSubmenu.value = false;
      showQuickBooksSubmenu.value = false;
      showTallySubmenu.value = false;
      showOriginalFilesSubmenu.value = false;
      showBusinessNavigatorSubmenu.value = false;
    }
  } else {
    // Direct export for formats without submenus
    selectFormatAndExport(format.value);
  }
};

const toggleExcelTypeOptions = (type) => {
  if (type === 'excel') {
    showExcelTypeOptions.value = !showExcelTypeOptions.value;
    showExcelDetailedTypeOptions.value = false;
  } else if (type === 'excel_detailed') {
    showExcelDetailedTypeOptions.value = !showExcelDetailedTypeOptions.value;
    showExcelTypeOptions.value = false;
  }
};

const exportWithType = (format, type) => {
  excelTypeFilter.value = type;
  selectFormatAndExport(format);
};

const selectFormatAndExport = async (format) => {
  if (loading.value) return; // Prevent multiple clicks
  selectedFormat.value = format;
  
  console.log('selectFormatAndExport called with format:', format);
  
  // Check for missing vendor codes if Business Navigator Counterparties or Excel is selected
  if (format === 'business_navigator_counterparties' || format === 'excel' || format === 'excel_detailed') {
    console.log('Checking for missing vendor codes...');
    try {
      const invoiceIds = getExportInvoiceIds();
      console.log('Invoice IDs to check:', invoiceIds);
      const checkPayload = { invoice_ids: invoiceIds };
      if ((format === 'excel' || format === 'excel_detailed') && excelTypeFilter.value !== 'all') {
        checkPayload.type_filter = excelTypeFilter.value;
      }
      const response = await api.post('/api/scans/export/check-vendor-codes', checkPayload);
      console.log('Vendor codes check response:', response.data);
      console.log('Missing count:', response.data.missing?.length);
      if (response.data.missing && response.data.missing.length > 0) {
        console.log('Found missing vendor codes:', response.data.missing.length);
        console.log('Missing data:', response.data.missing);
        missingInvoiceVendorCodes.value = response.data.missing;
        console.log('Setting showInvoiceVendorCodeModal to TRUE');
        showInvoiceVendorCodeModal.value = true;
        console.log('showInvoiceVendorCodeModal is now:', showInvoiceVendorCodeModal.value);
        return; // Don't proceed with export yet
      } else {
        console.log('No missing vendor codes');
      }
    } catch (err) {
      console.error('Failed to check vendor codes:', err);
      error.value = 'Failed to check vendor codes. Please try again.';
      return; // Don't proceed with export if check failed
    }
  }

  // Santa only: its line values have to go out without VAT. Where the document
  // gives no way to establish that, say so before the file is handed over rather
  // than let a gross figure through unnoticed.
  if (format === 'santa' || format === 'santa_purchases' || format === 'santa_sales') {
    if (!santaVatWarningAcknowledged.value) {
      try {
        const { data } = await api.post('/api/scans/export/check-santa-vat-lines', {
          invoice_ids: getExportInvoiceIds(),
        });

        if (data.count > 0) {
          santaVatLines.value = data.lines;
          showSantaVatModal.value = true;
          return; // wait for the accountant to decide
        }
      } catch (err) {
        // A failed check must not block the export; it is a warning, not a gate.
        console.error('Santa VAT check failed:', err);
      }
    }

    // Lines booked as a service (internal code U) go on a different account, so
    // the mapping is shown for a look-over before the file leaves. The mapping
    // itself is untouched — this only asks for a confirmation. Off unless the
    // setting is on, so nobody gets an extra click they did not ask for.
    if (authStore.confirmServiceLines && !santaServiceConfirmed.value) {
      try {
        const { data } = await api.post('/api/scans/export/check-santa-service-lines', {
          invoice_ids: getExportInvoiceIds(),
        });

        if (data.count > 0) {
          await openSantaServiceModal(data.lines);
          return; // wait for the accountant to confirm
        }
      } catch (err) {
        // Still not a gate — the file is more use than the warning. But a
        // failure here is indistinguishable from "no service lines found", and
        // that silence hid a missing route on a deployed server for a whole
        // afternoon. Say so, then carry on.
        console.error('Santa service line check failed:', err);
        $toast.warning(
          t('export.santaServiceCheckFailed', { status: err.response?.status || '—' }),
          { duration: 6000 }
        );
      }
    }
  }

  // Check for missing account code mappings if Santa format is selected
  if (format === 'santa' || format === 'santa_purchases' || format === 'santa_sales') {
    console.log('Checking for missing account code mappings...');
    try {
      const invoiceIds = getExportInvoiceIds();
      console.log('Invoice IDs for checking:', invoiceIds);
      const response = await api.post('/api/scans/export/check-account-mappings', {
        invoice_ids: invoiceIds,
        format: format // Send format to filter by type
      });
      console.log('Account mappings check response:', response.data);
      if (response.data.unmapped_items && response.data.unmapped_items.length > 0) {
        console.log('Found unmapped items:', response.data.unmapped_items.length);
        console.log('Unmapped items detail:', response.data.unmapped_items);
        // Add selectedAccountCode and selected properties to each item
        missingAccountItems.value = response.data.unmapped_items.map(item => ({
          ...item,
          selectedAccountCode: '',
          selected: false
        }));
        console.log('missingAccountItems after mapping:', missingAccountItems.value.length);
        
        // Filter account codes based on format
        let accountCodes = response.data.account_codes || [];
        console.log('Account codes before filtering:', accountCodes.length, accountCodes);
        console.log('Format:', format);
        if (format === 'santa_purchases') {
          accountCodes = accountCodes.filter(code => code.account_type === 'expense' || code.account_type === 'neutral');
          console.log('After expense/neutral filter:', accountCodes.length, accountCodes);
        } else if (format === 'santa_sales') {
          accountCodes = accountCodes.filter(code => code.account_type === 'income' || code.account_type === 'revenue' || code.account_type === 'neutral');
          console.log('After income/revenue/neutral filter:', accountCodes.length, accountCodes);
        }
        availableAccountCodes.value = accountCodes;
        console.log('availableAccountCodes set to:', availableAccountCodes.value.length, availableAccountCodes.value);
        
        console.log('Setting showAccountCodeModal to true');
        showAccountCodeModal.value = true;
        return; // Don't proceed with export yet
      } else {
        console.log('No unmapped items found');
      }
    } catch (err) {
      console.error('Failed to check account code mappings:', err);
      console.error('Error details:', err.response?.data);
      // Show error message to user
      if (err.response?.data?.error) {
        const errorMsg = err.response.data.error;
        // Translate error messages
        if (errorMsg.includes('No sales invoices found')) {
          error.value = t('export.noSalesInvoices');
        } else if (errorMsg.includes('No purchase invoices found')) {
          error.value = t('export.noPurchaseInvoices');
        } else if (errorMsg.includes('multiple companies')) {
          error.value = t('export.multipleCompaniesError');
        } else {
          error.value = errorMsg;
        }
        return; // Don't proceed with export
      }
    }
  }

  // Check for missing VAT exemption codes if Ajur sales format is selected
  if (format === 'ajur_sales') {
    console.log('Checking for invoices needing VAT exemption codes...');
    try {
      const invoiceIds = getExportInvoiceIds();
      console.log('Invoice IDs for VAT check:', invoiceIds);
      const response = await api.post('/api/scans/export/check-vat-codes', {
        invoice_ids: invoiceIds
      });
      console.log('VAT codes check response:', response.data);
      if (response.data.needs_vat_code && response.data.needs_vat_code.length > 0) {
        console.log('Found invoices needing VAT codes:', response.data.needs_vat_code.length);
        // Map the response data to ensure reactive properties
        invoicesNeedingVatCode.value = response.data.needs_vat_code.map(invoice => ({
          ...invoice,
          vat_exemption_code: invoice.current_vat_exemption_code,
          tax_exemption_reason: invoice.current_tax_exemption_reason || ''
        }));
        console.log('Mapped invoices:', invoicesNeedingVatCode.value);
        showVatExemptionModal.value = true;
        return; // Don't proceed with export yet
      } else {
        console.log('No invoices need VAT exemption codes');
      }
    } catch (err) {
      console.error('Failed to check VAT codes:', err);
      console.error('Error details:', err.response?.data);
      if (err.response?.data?.error) {
        error.value = err.response.data.error;
        return;
      }
    }
  }
  
  await handleExport();
};

const getExportInvoiceIds = () => {
  if (props.invoiceId) {
    return [props.invoiceId];
  }
  return props.invoiceIds || [];
};

const handleVendorCodesSaved = async () => {
  showVendorCodeModal.value = false;
  missingVendorCodes.value = [];
  await handleExport();
};

const handleInvoiceVendorCodesSaved = async () => {
  showInvoiceVendorCodeModal.value = false;
  missingInvoiceVendorCodes.value = [];
  
  // Emit updated event so parent (ScanDetails) can reload invoice data
  emit('updated');
  
  // Wait for parent to reload data
  await new Promise(resolve => setTimeout(resolve, 500));
  
  await handleExport();
};

const handleAccountCodesSaved = async () => {
  showAccountCodeModal.value = false;
  missingAccountItems.value = [];
  availableAccountCodes.value = [];
  await handleExport();
};

const handleVatCodesSaved = async () => {
  showVatExemptionModal.value = false;
  invoicesNeedingVatCode.value = [];
  await handleExport();
};

const fetchCompanyAccountCodes = async () => {
  try {
    const response = await api.get(`/api/companies/${props.companyId}/account-codes`);
    availableAccountCodes.value = response.data || [];
  } catch (err) {
    console.error('Failed to fetch account codes:', err);
  }
};

const handleAccountCodeCreated = async (newAccountCode) => {
  console.log('ExportModal - New account code created:', newAccountCode);
  console.log('ExportModal - Current account codes count:', availableAccountCodes.value.length);
  
  if (newAccountCode) {
    console.log('ExportModal - Adding new account code to list', {
      account_code: newAccountCode.account_code,
      internal_code: newAccountCode.internal_code
    });
    
    // Create a new array with the new code at the beginning for reactivity
    const updatedCodes = [newAccountCode, ...availableAccountCodes.value];
    availableAccountCodes.value = updatedCodes;
    
    console.log('ExportModal - After adding, account codes count:', availableAccountCodes.value.length);
    console.log('ExportModal - First code in list:', availableAccountCodes.value[0]?.account_code);
  }
};

const handleExport = async () => {
  if (!selectedFormat.value) return;

  // For Excel exports, only ask the accountant to confirm an accounting date
  // when at least one invoice falls outside the configured "Months back"
  // window. Recent-only batches export silently with each invoice's own date.
  //
  // Sales are excluded: an accounting date is posted against purchases, so the
  // prompt has nothing to set when the batch is explicitly a sales export.
  if (
    selectedFormat.value === 'excel'
    && excelTypeFilter.value !== 'sale'
    && authStore.useAccountingDate
    && !accountingDateConfirmed.value
    && hasInvoiceOlderThanMonthsBack()
  ) {
    showAccountingDateModal.value = true;
    return;
  }

  loading.value = true;
  error.value = null;

  try {
    // Determine invoice IDs and filter out any null/undefined values
    let invoiceIds = props.invoiceId ? [props.invoiceId] : props.invoiceIds;
    console.log('ExportModal - props.invoiceIds:', props.invoiceIds);
    console.log('ExportModal - props.invoiceId:', props.invoiceId);
    console.log('ExportModal - invoiceIds before filter:', invoiceIds);
    invoiceIds = (invoiceIds || []).filter(id => id != null);
    console.log('ExportModal - invoiceIds after filter:', invoiceIds);

    // Business Navigator Counterparties doesn't need invoice IDs
    if (selectedFormat.value !== 'business_navigator_counterparties' && invoiceIds.length === 0) {
      error.value = 'No invoices selected for export';
      loading.value = false;
      return;
    }

    // Handle original file formats separately
    if (['original_pdf', 'html', 'original_jpg', 'original_all'].includes(selectedFormat.value)) {
      const formatMap = {
        'original_pdf': ['pdf'],
        'html': ['html'],
        'original_jpg': ['jpg'],
        'original_all': ['pdf', 'html', 'jpg']
      };
      
      const response = await api.post('/api/scans/export/original', {
        invoice_ids: invoiceIds,
        formats: formatMap[selectedFormat.value]
      }, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Generate filename based on format
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      let filename = `original_invoices_${timestamp}.zip`;

      if (selectedFormat.value === 'original_pdf') {
        filename = invoiceIds.length === 1 ? `invoice_${timestamp}.pdf` : `invoices_pdf_${timestamp}.zip`;
      } else if (selectedFormat.value === 'html') {
        filename = invoiceIds.length === 1 ? `invoice_${timestamp}.html` : `invoices_html_${timestamp}.zip`;
      } else if (selectedFormat.value === 'original_jpg') {
        filename = invoiceIds.length === 1 ? `invoice_${timestamp}.jpg` : `invoices_jpg_${timestamp}.zip`;
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      loading.value = false;
      return;
    }

    // Handle all other export formats
    const exportPayload = (selectedFormat.value === 'business_navigator_counterparties' || selectedFormat.value === 'business_navigator_purchases' || selectedFormat.value === 'business_navigator_sales')
      ? { format: selectedFormat.value, invoice_ids: invoiceIds, company_id: props.companyId, locale: locale.value }
      : { invoice_ids: invoiceIds, format: selectedFormat.value, company_id: props.companyId, locale: locale.value };
    
    // Add type filter for Excel exports
    if ((selectedFormat.value === 'excel' || selectedFormat.value === 'excel_detailed') && excelTypeFilter.value !== 'all') {
      exportPayload.type_filter = excelTypeFilter.value;
    }

    // Attach accounting date when present (applies to any Excel export)
    if (selectedFormat.value === 'excel' && accountingDate.value) {
      exportPayload.accounting_date = accountingDate.value;
    }

    // Per-invoice overrides win over the global accounting date and the auto rule.
    if (selectedFormat.value === 'excel' && Object.keys(invoiceAccountingDates.value).length > 0) {
      exportPayload.accounting_dates = { ...invoiceAccountingDates.value };
    }

    // Account codes picked by hand on the service-confirmation screen. They
    // apply to this file only — the company's mappings stay as they are.
    if (Object.keys(santaServiceOverrides.value).length > 0) {
      exportPayload.account_code_overrides = { ...santaServiceOverrides.value };
    }

    const response = await api.post('/api/scans/export/bulk', exportPayload, {
      responseType: 'blob'
    });

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;

    // Generate filename based on format
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    let filename = `invoices_${timestamp}.zip`;

    if (selectedFormat.value === 'txt_pokupki') {
      filename = `POKUPKI_${timestamp}.txt`;
    } else if (selectedFormat.value === 'txt_prodagbi') {
      filename = `PRODAGBI_${timestamp}.txt`;
    } else if (selectedFormat.value === 'microinvest_xml') {
      filename = `microinvest_${timestamp}.xml`;
    } else if (selectedFormat.value === 'konto_xlsx') {
      filename = `Konto_${timestamp}.xlsx`;
    } else if (selectedFormat.value === 'excel') {
      const typeSuffix = excelTypeFilter.value !== 'all' ? `_${excelTypeFilter.value}` : '';
      filename = `Invoices${typeSuffix}_${timestamp}.xlsx`;
    } else if (selectedFormat.value === 'excel_detailed') {
      const typeSuffix = excelTypeFilter.value !== 'all' ? `_${excelTypeFilter.value}` : '';
      filename = `Invoices_Detailed${typeSuffix}_${timestamp}.zip`;
    } else if (selectedFormat.value === 'docdb') {
      filename = `DocDB_${timestamp}.xls`;
    } else if (selectedFormat.value === 'plusminus_csv') {
      filename = `PlusMinus_${timestamp}.csv`;
    } else if (selectedFormat.value === 'santa') {
      filename = `Santa_${timestamp}.xlsx`;
    } else if (selectedFormat.value === 'santa_purchases') {
      filename = `Santa_Purchases_${timestamp}.xlsx`;
    } else if (selectedFormat.value === 'santa_sales') {
      filename = `Santa_Sales_${timestamp}.xlsx`;
    } else if (selectedFormat.value === 'xero_csv') {
      filename = `xero_export_${timestamp}.csv`;
    } else if (selectedFormat.value === 'xero_xlsx') {
      filename = `xero_export_${timestamp}.xlsx`;
    } else if (selectedFormat.value === 'quickbooks_csv') {
      filename = `quickbooks_export_${timestamp}.csv`;
    } else if (selectedFormat.value === 'quickbooks_xlsx') {
      filename = `quickbooks_export_${timestamp}.xlsx`;
    } else if (selectedFormat.value === 'tally') {
      filename = `tally_export_${timestamp}.xml`;
    } else if (selectedFormat.value === 'tally_excel') {
      filename = `tally_export_${timestamp}.xlsx`;
    } else if (selectedFormat.value === 'tally_csv') {
      filename = `tally_export_${timestamp}.csv`;
    } else if (selectedFormat.value === 'ajur') {
      filename = `Ajur_${timestamp}.xlsx`;
    } else if (selectedFormat.value === 'ajur' || selectedFormat.value === 'ajur_excel') {
      filename = `ajur_export_${timestamp}.xlsx`;
    } else if (selectedFormat.value === 'ajur_sklad') {
      filename = `ajur_sklad_export_${timestamp}.zip`;
    } else if (selectedFormat.value === 'invoicescan_xml') {
      // Always returns single XML file with Documents/Document wrapper
      filename = `invoicescan_${timestamp}.xml`;
    } else if (selectedFormat.value === 'business_navigator') {
      filename = `business_navigator_${timestamp}.txt`;
    } else if (selectedFormat.value === 'business_navigator_purchases') {
      filename = `business_navigator_purchases_${timestamp}.txt`;
    } else if (selectedFormat.value === 'business_navigator_sales') {
      filename = `business_navigator_sales_${timestamp}.txt`;
    } else if (selectedFormat.value === 'business_navigator_counterparties') {
      filename = `counterparties_${timestamp}.txt`;
    } else if (selectedFormat.value === 'preciz_cx') {
      filename = `preciz_cx_${timestamp}.zip`;
    }

    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error('Export failed:', err);
    const errorMsg = err.response?.data?.message || 'Failed to export invoices. Please try again.';
    // Translate error messages
    if (errorMsg.includes('No sales invoices found')) {
      error.value = t('export.noSalesInvoices');
    } else if (errorMsg.includes('No purchase invoices found')) {
      error.value = t('export.noPurchaseInvoices');
    } else if (errorMsg.includes('multiple companies')) {
      error.value = t('export.multipleCompaniesError');
    } else {
      error.value = errorMsg;
    }
  } finally {
    loading.value = false;
    // Reset so a future Excel export re-prompts for the accounting date
    accountingDateConfirmed.value = false;
    invoiceAccountingDates.value = {};
  }
};

// Seller selection modal methods
const openSellerModal = (invoiceId) => {
  currentInvoiceId.value = invoiceId;
  showSellerModal.value = true;
};

const closeSellerModal = () => {
  showSellerModal.value = false;
  currentInvoiceId.value = null;
};

const handleSellerSelection = (selection) => {
  if (currentInvoiceId.value === null) return;
  
  const invoice = invoicesData.value.find(inv => inv.id === currentInvoiceId.value);
  if (!invoice) return;
  
  if (selection.type === 'company') {
    if (selection.data.name) invoice.vendor_name = selection.data.name;
    if (selection.data.vat_number) invoice.vendor_vat = selection.data.vat_number;
  } else if (selection.type === 'vendor') {
    if (selection.data.vendor_name) invoice.vendor_name = selection.data.vendor_name;
    if (selection.data.vendor_vat) invoice.vendor_vat = selection.data.vendor_vat;
  }
  
  closeSellerModal();
};

/* How far back the accounting date may be set. */
const monthsBackOptions = computed(() => [
  { value: 1, label: t('export.oneMonth', '1 месец') },
  { value: 3, label: t('export.threeMonths', '3 месеца') },
  { value: 6, label: t('export.sixMonths', '6 месеца') },
  { value: 12, label: t('export.twelveMonths', '12 месеца') },
]);
</script>

<style scoped>
.missing-info-warning {
  background-color: #fff3cd;
  /* border-radius removed */
  padding: 12px 16px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #856404;
}

.missing-info-warning svg { display: none; }

.missing-info-warning strong {
  color: #856404;
  margin-right: 4px;
  flex-shrink: 0;
}

.missing-info-warning span {
  color: #856404;
}

.invalid-vat-warning {
  background-color: #fee;

  /* border-radius removed */
  padding: 12px 16px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #721c24;
}

.invalid-vat-warning svg { display: none; }

.invalid-vat-warning strong {
  color: #dc3545;
  margin-right: 4px;
  flex-shrink: 0;
}

.invalid-vat-warning span {
  color: #721c24;
}

.vat-conflict-warning {
  background-color: #fff3cd;

  /* border-radius removed */
  padding: 12px 16px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #856404;
}

.vat-conflict-warning svg { display: none; }

.vat-conflict-warning strong {
  color: #856404;
  margin-right: 4px;
  flex-shrink: 0;
}

.vat-conflict-warning span {
  color: #856404;
}

.missing-info-warning {
  background-color: #fff3cd;
  /* border-radius removed */
  padding: 12px 16px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #856404;
}

.missing-info-warning svg { display: none; }

.missing-info-warning strong {
  color: #856404;
  margin-right: 4px;
  flex-shrink: 0;
}

.missing-info-warning span {
  color: #856404;
}

.invalid-vat-warning {
  background-color: #fee;
  /* border-radius removed */
  padding: 12px 16px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #721c24;
}

.invalid-vat-warning svg { display: none; }

.invalid-vat-warning strong {
  color: #dc3545;
  margin-right: 4px;
  flex-shrink: 0;
}

.invalid-vat-warning span {
  color: #721c24;
}

.vat-conflict-warning {
  background-color: #fff3cd;
  /* border-radius removed */
  padding: 12px 16px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #856404;
}

.vat-conflict-warning svg { display: none; }

.vat-conflict-warning strong {
  color: #856404;
  margin-right: 4px;
  flex-shrink: 0;
}

.vat-conflict-warning span {
  color: #856404;
}

.invoices-table-container {
  margin-top: 20px;
  overflow-x: scroll;
  border: 1px solid var(--border-color);
  /* border-radius removed */
  scrollbar-gutter: stable;
}

.invoices-table-container::-webkit-scrollbar {
  height: 8px;
  display: block;
}

.invoices-table-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  display: block;
}

.invoices-table-container::-webkit-scrollbar-thumb {
  background: #888;
  display: block;
}

.invoices-table-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.invoices-review-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  table-layout: fixed;
}

.invoices-review-table thead {
  background-color: var(--bg-secondary);
  position: sticky;
  top: 0;
  z-index: 1;
}

.invoices-review-table th {
  padding: 8px 10px;
  text-align: left;
  font-weight: 600;
  color: var(--text-primary);
  border-bottom: 2px solid var(--border-color);
  white-space: nowrap;
}

.invoices-review-table th:nth-child(1) { width: 12%; } /* Invoice Number */
.invoices-review-table th:nth-child(2) { width: 10%; }  /* Date */
.invoices-review-table th:nth-child(3) { width: 11%; } /* Company VAT */
.invoices-review-table th:nth-child(4) { width: 11%; } /* Partner VAT */
.invoices-review-table th:nth-child(5) { width: 20%; } /* Partner Name */
.invoices-review-table th:nth-child(6) { width: 6%; }  /* Currency */
.invoices-review-table th:nth-child(7) { width: 10%; } /* VAT Amount */
.invoices-review-table th:nth-child(8) { width: 10%; } /* Total Amount */
.invoices-review-table th:nth-child(9) { width: 10%; text-align: center; } /* Actions */

.invoices-review-table td {
  padding: 8px 10px;
  border-bottom: 1px solid var(--border-light);
  color: var(--text-secondary);
  overflow: visible;
}

.invoices-review-table tbody tr:hover {
  background-color: var(--bg-hover);
}

.invoices-review-table tbody tr:last-child td {
  border-bottom: none;
}

.invoices-review-table td.amount {
  text-align: right;
  font-weight: 500;
  font-family: 'Courier New', monospace;
}

.invoices-review-table td.missing-data {
  background-color: #fee;
  color: #dc3545;
  font-weight: 600;
}

.invoices-review-table td.conflict-vat {
  background-color: #ffeb3b;
  color: #856404;
  font-weight: 600;
}

td.conflict-vat .editable-field {
  background-color: #ffeb3b !important;
  border-color: #ffc107 !important;
}

.conflict-vat-row {
  background-color: rgba(255, 235, 59, 0.2);
}

.conflict-vat-row:hover {
  background-color: rgba(255, 235, 59, 0.3);
}

.invoices-review-table td.unique-name {
  background-color: #ffebee;
  color: #c62828;
  font-weight: 600;
}

td.unique-name .editable-field {
  background-color: #ffebee !important;
  border-color: #ef5350 !important;
}

.type-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
}

.type-badge.type-invoice {
  background-color: #e3f2fd;
  color: #1976d2;
}

.type-badge.type-credit_note {
  background-color: #fff3e0;
  color: #f57c00;
}

.type-badge.type-debit_note {
  background-color: #fce4ec;
  color: #c2185b;
}

.type-badge.type-receipt {
  background-color: #e8f5e9;
  color: #388e3c;
}

.type-badge.type-unknown {
  background-color: #f5f5f5;
  color: #757575;
}

.editable-field {
  width: 100%;
  padding: 4px 6px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
  font-family: inherit;
  background-color: white;
  transition: border-color 0.2s;
}

.editable-field:focus {
  outline: none;
  border-color: #4a90e2;
  box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.1);
}

.editable-field:hover {
  border-color: #999;
}

.missing-data .editable-field {
  background-color: #fee;
  border-color: #dc3545;
}

.missing-data .editable-field:focus {
  background-color: white;
  border-color: #4a90e2;
}

input[type="number"].editable-field {
  text-align: left;
}

.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px;
  color: var(--text-secondary);
}

.no-data {
  text-align: center;
  padding: 40px;
  color: var(--text-secondary);
  font-style: italic;
}

.format-option {
  cursor: pointer;
  transition: all 0.2s ease;
}

.format-option.has-submenu {
  cursor: pointer;
  position: relative;
}

/* The card does not lift or cast a shadow: a lift is the gesture a mark on
   its own uses, and a card the size of a paragraph rising two pixels under
   the pointer reads as the grid shifting. The blue edge says enough. */
.format-option:hover:not(.loading) {
  transform: none;
  box-shadow: none;
}

.format-option .format-icon svg {
  transition: transform 0.15s ease;
}

.format-option:hover:not(.loading) > .format-content .format-icon svg {
  transform: scale(1.18);
}

.format-option.submenu-open {
  box-shadow: none;
  border-color: var(--primary);
}

.format-option.loading {
  pointer-events: none;
  opacity: 0.6;
}

.format-content {
  position: relative;
}

.format-arrow {
  position: absolute;
  right: -32px;
  top: 4px;
  color: var(--text-secondary);
  font-size: 14px;
  transition: transform 0.2s ease;
}

/* An opened format, at the same scale as the card it hangs under.
 *
 * It was a stack of grey slabs: 12px padding on every row, a filled
 * background for each one and a third, darker fill for the options inside
 * them — boxes within boxes within a box. The rows are parted by a hairline
 * now and fill nothing; the one that is chosen keeps a light tint. */
.format-submenu {
  margin-top: 8px;
  padding-top: 6px;
  border-top: 1px solid #f1f5f9;
}

.submenu-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  flex-shrink: 0;
}

.submenu-expand-arrow {
  margin-left: auto;
  color: #999;
  font-size: 0.7rem;
  flex-shrink: 0;
}

.submenu-item.type-open {
  background-color: #f4f9fd;
  flex-wrap: wrap;
}

.type-options {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-top: 6px;
  padding-top: 4px;
  border-top: 1px solid #e3ecf4;
}

/* Purchases or sales: a line of text with its mark, indented under the
   format it belongs to — not a third filled slab. */
.type-option {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 5px 8px 5px 22px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
  background-color: transparent;
  transition: background 0.15s ease;
}

.type-option:hover {
  background-color: #fff;
}

.type-option-icon {
  width: auto;
  color: var(--primary);
  flex-shrink: 0;
}

.type-option-icon svg {
  width: 13px;
  height: 13px;
  transition: transform 0.15s ease;
}

.type-option:hover .type-option-icon svg {
  transform: scale(1.18);
}

.submenu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  margin: 0;
  border-radius: 6px;
  transition: background 0.15s ease;
  background-color: transparent;
  position: relative;
}

.submenu-item + .submenu-item {
  border-top: 1px solid #f1f5f9;
}

.submenu-item:hover {
  background-color: #fbfcfd;
}

.submenu-item.selected {
  background-color: #f4f9fd;
  border-left: none;
  box-shadow: inset 2px 0 0 0 var(--primary);
}

.submenu-item-main {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex: 1;
  min-width: 0;
  cursor: pointer;
}

/* The row does not slide sideways under the pointer — nothing else in the
   app moves on hover except a mark standing on its own. */
.submenu-item-main:hover {
  transform: none;
}

.submenu-settings-btn {
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
  color: var(--text-secondary, #64748b);
  flex-shrink: 0;
}

.submenu-settings-btn svg {
  width: 13px;
  height: 13px;
}

/* The app's one hover for a mark on its own. */
.submenu-settings-btn:hover {
  background: transparent;
  color: var(--primary);
  border-color: transparent;
  transform: translateY(-2px);
}

.submenu-icon {
  color: var(--primary);
  width: auto;
  margin-top: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.submenu-icon svg {
  width: 15px;
  height: 15px;
  transition: transform 0.15s ease;
}

/* Hovering a row grows its mark a little. The row itself holds still — the
   mark is what answers the pointer. */
.submenu-item:hover .submenu-icon svg,
.submenu-item-main:hover .submenu-icon svg {
  transform: scale(1.18);
}

.submenu-details {
  flex: 1;
}

.submenu-name {
  font-size: 12px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0;
}

.submenu-description {
  font-size: 11px;
  color: var(--text-secondary);
  line-height: 1.4;
}

.format-loading {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
}

.loading-message {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 20px;
  color: var(--text-secondary);
  font-size: 14px;
}

.spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid var(--border-color);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.btn-success {
  background-color: #10b981;
  color: white;
  margin-right: 8px;
}

.btn-success:hover:not(:disabled) {
  background-color: #059669;
}

.btn-success:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.flex {
  display: flex;
}

.items-center {
  align-items: center;
}

.gap-2 {
  gap: 0.5rem;
}

.btn-view {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  min-width: 32px;
  padding: 4px 8px;
  border: none;
  border-radius: 4px;
  background-color: #4a90e2;
  color: white;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-view:hover {
  background-color: #357abd;
}

.btn-view:active {
  transform: translateY(0);
}

.select-seller-btn {
  padding: 4px 8px;
  min-width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  background-color: #4a90e2;
  color: white;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;
}

.select-seller-btn:hover {
  background-color: #357abd;
}

/* Accounting Date Modal */
/* Matches how .accounting-date-modal sizes itself — the width belongs on the
   inner content box, not on this wrapper. */
.santa-vat-modal .modal-content {
  max-width: 780px;
}

/* The service list carries a selector and a five-decimal amount, neither of
   which fits the narrower VAT layout. */
.santa-service-modal .modal-content {
  max-width: min(1120px, 94vw);
}

.santa-vat-hint {
  margin: 0 0 16px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
  font-size: 13.5px;
  line-height: 1.55;
}

.santa-vat-table {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
}

.santa-vat-row {
  display: grid;
  grid-template-columns: 1.4fr 2fr 0.5fr 0.9fr;
  gap: 12px;
  padding: 10px 14px;
  font-size: 13px;
  align-items: baseline;
}

.santa-vat-row + .santa-vat-row { border-top: 1px solid #f3f4f6; }

.santa-vat-row-head {
  background: #f9fafb;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
}

.sv-col-invoice { display: flex; flex-direction: column; gap: 2px; }
.sv-link { color: #2b82f0; font-weight: 600; text-decoration: none; }
.sv-link:hover { text-decoration: underline; }
.sv-seller { font-size: 11px; color: #9ca3af; }
.sv-col-item { color: #374151; }
.sv-col-qty, .sv-col-total { text-align: right; color: #111827; font-weight: 600; }

/* The service list trades the quantity column for the code being confirmed,
   which is a selector rather than text and needs the room. The amount is given
   a fixed track: it carries five decimals and must never be the column that
   gives way. */
.santa-vat-row.santa-service-row {
  grid-template-columns: minmax(130px, 1fr) minmax(180px, 1.8fr) minmax(220px, 1.6fr) 150px;
  align-items: center;
}
.santa-vat-row.santa-service-row.santa-vat-row-head { align-items: baseline; }

.santa-service-row .sv-col-total {
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.sv-col-code { display: flex; flex-direction: column; gap: 3px; min-width: 0; }

.sv-changed {
  font-size: 11px;
  color: #92400e;
}

.sv-error {
  margin: 0 0 10px;
  padding: 8px 14px;
  font-size: 12.5px;
  color: #b91c1c;
}

.santa-vat-row-head .sv-col-code { color: inherit; }

.accounting-date-modal .modal-container {
  max-width: 720px;
}

.accounting-date-hint {
  margin: 0 0 16px 0;
  padding: 12px 16px;
  background: linear-gradient(135deg, #dbeafe 0%, #f0f9ff 100%);
  border-left: 4px solid var(--primary, #0ea5e9);
  color: var(--text-primary);
  font-size: 13px;
  line-height: 1.5;
}

.accounting-date-modal .form-group {
  margin-top: 8px;
}

.accounting-date-modal .form-group label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
  color: var(--text-primary);
}

.accounting-date-modal .form-control {
  width: 100%;
  padding: 10px 12px;
  border: 2px solid var(--border);
  border-radius: 8px;
  font-size: 14px;
  color: var(--text-primary);
  transition: all 0.2s ease;
}

.accounting-date-modal .form-control:focus {
  outline: none;
  border-color: var(--primary, #0ea5e9);
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.accounting-date-invoice-list {
  margin-top: 20px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: white;
  overflow: hidden;
}

.accounting-date-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f8fafc;
  border-bottom: 1px solid var(--border);
  font-size: 13px;
  color: var(--text-primary);
}

.accounting-date-table {
  max-height: 340px;
  overflow-y: auto;
}

.accounting-date-row {
  display: grid;
  grid-template-columns: 36px 1fr 140px 180px;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-bottom: 1px solid var(--border);
  font-size: 13px;
  color: var(--text-primary);
}

.accounting-date-row:last-child {
  border-bottom: 0;
}

.accounting-date-row:hover:not(.accounting-date-row-head) {
  background: #f8fafc;
}

.accounting-date-row-head {
  position: sticky;
  top: 0;
  background: #f1f5f9;
  color: var(--text-secondary);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 10px 16px;
  z-index: 1;
}

.accounting-date-col-idx {
  text-align: right;
  color: var(--text-secondary);
  font-variant-numeric: tabular-nums;
}

.accounting-date-col-number {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.accounting-date-col-doc-date {
  color: var(--text-secondary);
  font-variant-numeric: tabular-nums;
}

.accounting-date-col-muted {
  color: #cbd5e1;
}

.accounting-date-col-post-date {
  display: flex;
  align-items: center;
  gap: 6px;
}

.accounting-date-row-input {
  flex: 1;
  min-width: 0;
  padding: 6px 10px;
  border: 1.5px solid var(--border);
  border-radius: 6px;
  font-size: 13px;
  color: var(--text-primary);
  background: white;
  transition: all 0.15s ease;
  font-variant-numeric: tabular-nums;
}

.accounting-date-row-input:focus {
  outline: none;
  border-color: var(--primary, #0ea5e9);
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.accounting-date-row-reset {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid var(--border);
  border-radius: 6px;
  color: var(--text-secondary);
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s ease;
}

.accounting-date-row-reset:hover {
  background: #fef2f2;
  border-color: #fecaca;
  color: #dc2626;
}

@media (max-width: 640px) {
  .accounting-date-row {
    grid-template-columns: 28px 1fr 140px;
    grid-template-areas:
      "idx number docdate"
      ".   post   post";
    gap: 6px 8px;
  }
  .accounting-date-col-idx { grid-area: idx; }
  .accounting-date-col-number { grid-area: number; }
  .accounting-date-col-doc-date { grid-area: docdate; }
  .accounting-date-col-post-date { grid-area: post; }
  .accounting-date-row-head { display: none; }
}

/* Warning Confirmation Modal */
.warning-confirmation-modal .modal-container {
  max-width: 500px;
}

.warning-confirmation-content {
  padding: 24px 0;
}

.warning-confirmation-content .warning-message {
  margin: 12px 0;
  padding: 12px 16px;
  /* border-radius removed */
  font-size: 14px;
  line-height: 1.5;
}

.warning-confirmation-content .warning-message-error {
  background-color: #fef2f2;
  border-left: 4px solid #dc3545;
  color: #991b1b;
}

.warning-confirmation-content .warning-message-error strong {
  color: #dc3545;
  font-weight: 600;
}

.warning-confirmation-content .warning-message-warning {
  background-color: #fefce8;
  border-left: 4px solid #f59e0b;
  color: #92400e;
}

.warning-confirmation-content .warning-message-warning strong {
  color: #d97706;
  font-weight: 600;
}

.warning-confirmation-content .confirmation-question {
  margin-top: 24px;
  margin-bottom: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  text-align: center;
}

/* Responsive Styles for screens less than 1350px */
@media (max-width: 1349px) {
  .invoices-table-container {
    overflow-x: scroll;
    -webkit-overflow-scrolling: touch;
  }

  .invoices-review-table {
    min-width: 1200px;
  }
}

/* VAT Validation Indicators */
.vat-indicator-inline {
  display: inline-flex;
  align-items: center;
  margin-left: 4px;
  font-size: 14px;
  cursor: help;
  vertical-align: middle;
}

.vat-indicator-inline.vat-valid {
  color: #10b981;
}

.vat-indicator-inline.vat-invalid {
  color: #ef4444;
}

.vat-invalid-cell {
  color: #ef4444 !important;
  font-weight: 600;
}

.vat-invalid-cell input {
  color: #ef4444 !important;
  font-weight: 600;
  border-color: #ef4444 !important;
}


/* ── The dashboard's dress ─────────────────────────────────────────────
 *
 * One card, one 12px radius, hairline edges, and controls at the table's
 * scale: 13px labels in 32px boxes, one filled button and the rest quiet.
 * A dialogue is opened from the table and should read as the same product.
 *
 * Last in the file on purpose — these do not delete what the modal set for
 * itself above, they settle the frame, the heading and the buttons. */
.modal-content,
.modal-container,
.modal-content-large {
    border: 1px solid #dde3ea;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}

.modal-header {
    padding: 12px 14px;
    border-bottom: 1px solid #dde3ea;
    background: #fff;
}

.modal-header h2,
.modal-header h3,
.modal-header h4,
.modal-title {
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
}

.modal-subtitle {
    margin: 1px 0 0;
    font-size: 11px;
    font-weight: 400;
    color: #64748b;
}

.modal-body {
    padding: 12px 14px;
}

.modal-footer {
    padding: 10px 14px;
    gap: 8px;
    background: #fff;
    border-top: 1px solid #dde3ea;
}

/* The close: a mark on its own, with the lift the rest of the app has. */
.modal-header .btn-icon,
.btn-close,
.modal-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    min-width: 26px;
    padding: 0;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 8px;
    color: #64748b;
    box-shadow: none;
    transition: all 0.15s ease;
}

.modal-header .btn-icon:hover,
.btn-close:hover,
.modal-close:hover {
    background: transparent;
    color: #0697e4;
    transform: translateY(-2px);
}

/* The buttons: the same box everywhere, filled only where something is
   being committed. */
.btn-primary,
.btn-secondary,
.btn-cancel,
.btn-cancel-edit,
.btn-save,
.btn-save-edit,
.btn-confirm,
.btn-danger,
.btn-success,
.btn-add {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    height: 32px;
    padding: 0 14px;
    border-radius: 8px;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    line-height: 1;
    box-shadow: none;
    transition: all 0.15s ease;
}

.btn-secondary,
.btn-cancel,
.btn-cancel-edit {
    background: #fff;
    border: 1px solid #dde3ea;
    color: #1e293b;
}

.btn-secondary:hover:not(:disabled),
.btn-cancel:hover:not(:disabled),
.btn-cancel-edit:hover:not(:disabled) {
    background: #fff;
    border-color: #0697e4;
    color: #0697e4;
    box-shadow: none;
    transform: none;
}

.btn-primary,
.btn-confirm,
.btn-save,
.btn-save-edit,
.btn-add {
    background: #0697e4;
    border: 1px solid #0697e4;
    color: #fff;
}

.btn-primary:hover:not(:disabled),
.btn-confirm:hover:not(:disabled),
.btn-save:hover:not(:disabled),
.btn-save-edit:hover:not(:disabled),
.btn-add:hover:not(:disabled) {
    background: #0b84c7;
    border-color: #0b84c7;
    box-shadow: none;
    transform: none;
}

.btn-success {
    background: #10b981;
    border: 1px solid #10b981;
    color: #fff;
}

.btn-success:hover:not(:disabled) {
    background: #0ea371;
    border-color: #0ea371;
    box-shadow: none;
    transform: none;
}

.btn-danger {
    background: #e04545;
    border: 1px solid #e04545;
    color: #fff;
}

.btn-danger:hover:not(:disabled) {
    background: #c93a3a;
    border-color: #c93a3a;
    box-shadow: none;
    transform: none;
}

/* ── Fields at the table's scale ───────────────────────────────────────
 *
 * The dialogues were built with 16px type in 44px boxes, which next to the
 * 13px rows of the table reads as a different application. Same hairline,
 * same 8px corner, same type size as the controls on the dashboard. */
.modal-body input[type="text"],
.modal-body input[type="number"],
.modal-body input[type="email"],
.modal-body input[type="tel"],
.modal-body input[type="url"],
.modal-body input[type="search"],
.modal-body input[type="date"],
.modal-body input[type="password"],
.modal-body select,
.modal-body textarea {
    padding: 7px 10px;
    border: 1px solid #dde3ea;
    border-radius: 8px;
    font-family: inherit;
    font-size: 13px;
    color: #1e293b;
    background: #fff;
    box-shadow: none;
}

.modal-body input[type="text"]:focus,
.modal-body input[type="number"]:focus,
.modal-body input[type="email"]:focus,
.modal-body input[type="tel"]:focus,
.modal-body input[type="url"]:focus,
.modal-body input[type="search"]:focus,
.modal-body input[type="date"]:focus,
.modal-body input[type="password"]:focus,
.modal-body select:focus,
.modal-body textarea:focus {
    outline: none;
    border-color: #0697e4;
    box-shadow: none;
}

.modal-body label,
.modal-body .form-label {
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
}

/* The corners.
 *
 * The card is rounded but does not clip, so the header's own white — and the
 * footer's — was painted square and stood a pixel or two outside the curve.
 * The bands carry the same radius on the side they sit on. Clipping the card
 * instead would cut off anything a dialogue drops below its edge. */
.modal-header {
    border-radius: 12px 12px 0 0;
}

.modal-footer,
.modal-actions {
    border-radius: 0 0 12px 12px;
}

/* ── One hover for a mark on its own ───────────────────────────────────
 *
 * Wherever a button is just an icon — a row's edit or delete, a field's
 * helper, a dialogue's close — hovering turns the mark its own colour and
 * lifts it two pixels. Nothing is drawn behind it. Written last, and the
 * danger variant written with both of its classes, because the rules being
 * answered are themselves .btn-icon.btn-icon-danger. */
.btn-icon:hover:not(:disabled) {
  background: transparent;
  border-color: transparent;
  color: var(--primary, #0697e4);
  box-shadow: none;
  transform: translateY(-2px);
}

.btn-icon.btn-icon-danger:hover,
.btn-icon.btn-icon-danger:hover:not(:disabled) {
  background: transparent;
  border-color: transparent;
  color: #e04545;
  box-shadow: none;
  transform: translateY(-2px);
}

.btn-icon svg {
  transition: transform 0.15s ease;
}

/* ── One frame, not two ────────────────────────────────────────────────
 *
 * The chrome above gives a hairline, a 12px corner and a shadow to both
 * .modal-content and .modal-container — but in this dialogue the container
 * is only the box that centres the card, and in one case it is far wider
 * than the card, so its edge was drawn as a rectangle standing in the empty
 * space beside the dialogue. The card keeps the frame; the wrapper carries
 * none. */
.modal-container {
  background: transparent;
  border: none;
  border-radius: 0;
  box-shadow: none;
}
</style>



